/**
 * ============================================================
 * SHATOKB · KOI — Experta K-Beauty con IA
 * Archivo: assets/shatokb-koi-chat.js
 * Version: 4.6 — Swiss-watch reliability:
 *               1. enviarSkinReport() ahora retorna Promise<token|null> — async real.
 *               2. confirmarEmailCarrito() hace await del token antes de proceder.
 *               3. shatokb-quiz.js tiene waitForToken() con retry/polling 8s como
 *                  red de seguridad por si el network tarda más de lo esperado.
 *               Sin setTimeout fijos. El carrito avanza SOLO cuando el token existe.
 *
 * Arquitectura:
 *   - Este archivo corre en el browser (Shopify)
 *   - Las llamadas a OpenAI van a través del Cloudflare Worker
 *     (nunca directamente — la API key es privada)
 *   - El contexto del quiz se inyecta automáticamente
 *     desde window.SHATOKB_RESULTADO (shatokb-quiz.js)
 * ============================================================
 */

(function () {
  'use strict';
  console.log('%c[KOI] shatokb-koi-chat.js v4.8 cargado ✅ — Gate3 v96: modal 100% autosuficiente, NO depende del panel KOI', 'color:#e75480;font-weight:bold;font-size:13px');

  /* ── Configuración ──────────────────────────────────────── */
  const KOI_CONFIG = {
    // Cloudflare Worker URL — secured proxy to OpenAI
    workerUrl:  'https://koi-proxy.luisfonse2010.workers.dev/chat',
    reportUrl:  'https://koi-proxy.luisfonse2010.workers.dev/report',

    // URL base del sitio (para construir el link del reporte)
    siteUrl:    'https://shatokb.com',

    // URL de la tabla API (Genspark project — tabla skin_reports)
    tableApiUrl: 'https://www.genspark.ai/api/v1/project/8b11bf35-e038-4848-9fe1-fffa1edac409',

    // API Key de Genspark para autenticar el POST a la tabla skin_reports
    // (El Worker la incluye en el header Authorization: Bearer <key>)
    // Obtener en: genspark.ai → proyecto → Settings → API Keys
    tableApiKey: '',  // ← RELLENAR si la tabla requiere autenticación

    // Límite de mensajes en el historial (memoria de conversación)
    maxHistory: 20,

    // Delay antes de que KOI aparezca (ms)
    appearDelay: 1800,

    // Delay antes del primer mensaje de KOI (ms)
    firstMsgDelay: 600,
  };

  /* ── Estado global de KOI ───────────────────────────────── */
  const KOI_STATE = {
    historial:      [],     // [{role, content}]
    contexto:       null,   // perfil del quiz
    isTyping:       false,  // KOI está escribiendo
    isReady:        false,  // chat inicializado
    msgCount:       0,      // contador de mensajes enviados
    // ── The Reveal ──
    revealPhase:    'insight', // 'insight' | 'email' | 'revealed'
    emailCaptured:  '',     // email capturado en el chat
    // ── Vision Analysis ──
    visionResult:   null,   // resultado del Worker /vision {mensaje_koi, mensaje_reveal, dimensiones, ...}
  };

  /* ── Chips localizados por idioma ───────────────────────── */
  const KOI_CHIPS_I18N = {
    en: {
      // ── The Reveal: bifurcación inicial ──
      reveal:      ['📸 Analyze my skin first', '✨ Reveal my routine'],
      // ── Post-cámara: después del mensaje persuasivo ──
      post_camara: ['📸 Yes, analyze my face', '✨ Show me my routine'],
      // ── Post-visión: después del análisis facial, solo reveal ──
      post_vision: ['✦ See the products chosen for me →'],
      // ── Post-reveal: explorar rutina ──
      bienvenida:  ['Walk me through my routine', 'Why these specific products?', 'Explain the key ingredients', 'How long until I see results?'],
      post_rutina: ['What order do I apply them?', 'AM vs PM — what changes?', 'Are any of these pregnancy-safe?', 'Which one should I start with?'],
      objeciones:  ['Which product matters most?', "I've never tried K-Beauty", 'Can I combine these ingredients?', 'I have a different question'],
    },
    es: {
      reveal:      ['📸 Analiza mi piel primero', '✨ Descubre mi rutina'],
      post_camara: ['📸 Sí, analiza mi rostro', '✨ Ver mi rutina ya'],
      post_vision: ['✦ Ver los productos elegidos para mí →'],
      bienvenida:  ['Explícame mi rutina paso a paso', '¿Por qué estos productos?', 'Explícame los ingredientes clave', '¿Cuánto tiempo hasta ver resultados?'],
      post_rutina: ['¿En qué orden los aplico?', 'AM vs PM — ¿qué cambia?', '¿Son seguros en el embarazo?', '¿Con cuál empiezo?'],
      objeciones:  ['¿Cuál es el producto más importante?', 'Nunca he probado K-Beauty', '¿Puedo combinar estos ingredientes?', 'Tengo otra pregunta'],
    },
    fr: {
      reveal:      ['📸 Analyser ma peau d\'abord', '✨ Révéler ma routine'],
      post_camara: ['📸 Oui, analyser mon visage', '✨ Voir ma routine'],
      post_vision: ['✦ Voir les produits choisis pour moi →'],
      bienvenida:  ['Expliquez-moi ma routine', 'Pourquoi ces produits ?', 'Expliquez les ingrédients clés', 'Combien de temps pour voir les résultats ?'],
      post_rutina: ['Dans quel ordre les appliquer ?', 'Matin vs soir — qu\'est-ce qui change ?', 'Sont-ils sûrs pendant la grossesse ?', 'Par lequel commencer ?'],
      objeciones:  ['Quel produit est le plus important ?', "Je n'ai jamais essayé la K-Beauty", 'Puis-je combiner ces ingrédients ?', "J'ai une autre question"],
    },
    pt: {
      reveal:      ['📸 Analisar minha pele primeiro', '✨ Revelar minha rotina'],
      post_camara: ['📸 Sim, analise meu rosto', '✨ Ver minha rotina agora'],
      post_vision: ['✦ Ver os produtos escolhidos para mim →'],
      bienvenida:  ['Explique minha rotina passo a passo', 'Por que esses produtos?', 'Explique os ingredientes principais', 'Quanto tempo para ver resultados?'],
      post_rutina: ['Em que ordem aplicar?', 'AM vs PM — o que muda?', 'São seguros na gravidez?', 'Com qual devo começar?'],
      objeciones:  ['Qual produto é mais importante?', 'Nunca experimentei K-Beauty', 'Posso combinar esses ingredientes?', 'Tenho outra pergunta'],
    },
    de: {
      reveal:      ['📸 Zuerst meine Haut analysieren', '✨ Meine Routine enthüllen'],
      post_camara: ['📸 Ja, analysiere mein Gesicht', '✨ Routine jetzt anzeigen'],
      post_vision: ['✦ Die für mich ausgewählten Produkte sehen →'],
      bienvenida:  ['Erkläre mir meine Routine', 'Warum genau diese Produkte?', 'Erkläre die wichtigsten Inhaltsstoffe', 'Wann sehe ich erste Ergebnisse?'],
      post_rutina: ['In welcher Reihenfolge auftragen?', 'Morgen vs. Abend — was ändert sich?', 'Sind sie in der Schwangerschaft sicher?', 'Mit welchem soll ich anfangen?'],
      objeciones:  ['Welches Produkt ist am wichtigsten?', 'Ich habe K-Beauty noch nie ausprobiert', 'Kann ich diese Inhaltsstoffe kombinieren?', 'Ich habe eine andere Frage'],
    },
    it: {
      reveal:      ['📸 Analizza prima la mia pelle', '✨ Rivela la mia routine'],
      post_camara: ['📸 Sì, analizza il mio viso', '✨ Mostrami la mia routine'],
      post_vision: ['✦ Vedere i prodotti scelti per me →'],
      bienvenida:  ['Spiegami la mia routine', 'Perché questi prodotti?', 'Spiegami gli ingredienti chiave', 'Quanto tempo per vedere i risultati?'],
      post_rutina: ['In che ordine applicarli?', 'Mattina vs sera — cosa cambia?', 'Sono sicuri in gravidanza?', 'Da quale inizio?'],
      objeciones:  ['Qual è il prodotto più importante?', 'Non ho mai provato la K-Beauty', 'Posso combinare questi ingredienti?', 'Ho un\'altra domanda'],
    },
  };

  /* ── Chips de respuesta rápida por situación (con i18n) ─── */
  const KOI_CHIPS = {
    bienvenida:  [],
    post_rutina: [],
    objeciones:  [],
  };

  /* ══════════════════════════════════════════════════════════
     INICIALIZACIÓN
     ══════════════════════════════════════════════════════════ */

  /**
   * Punto de entrada principal.
   * Llamado desde shatokb-quiz.js cuando se muestra el resultado.
   */
  window.shatokbIniciarKOI = function (contextoQuiz) {
    // Guard anti-duplicación: bloquea cualquier segunda llamada
    // (el quiz dispara CustomEvent + intentarKOI en paralelo).
    // Se activa AQUÍ, inmediatamente, antes de cualquier setTimeout.
    if (KOI_STATE.isReady) return;
    KOI_STATE.isReady = true; // ← marcar YA, no esperar al setTimeout

    // Guardar contexto del quiz
    KOI_STATE.contexto = contextoQuiz || obtenerContextoLocal();

    // Crear y montar el DOM del chat
    // crearDOM tiene retry interno si el contenedor no existe aún
    function iniciarConWrapper(w) {
      if (!w) return;
      // Animar entrada después del delay
      setTimeout(() => {
        w.classList.add('koi--visible');
        // Primer mensaje proactivo de KOI
        setTimeout(() => {
          enviarMensajeKOI_proactivo();
        }, KOI_CONFIG.firstMsgDelay);
      }, KOI_CONFIG.appearDelay);
    }

    const wrapper = crearDOM(0);
    if (wrapper) {
      iniciarConWrapper(wrapper);
    } else {
      // Si crearDOM retornó null, esperar a que el retry interno lo cree
      // Hacemos polling para detectar cuando el wrapper aparece en el DOM
      var pollIniciar = 0;
      var pollTimer = setInterval(function() {
        pollIniciar++;
        var w = document.getElementById('shatokb-koi-wrapper');
        if (w) {
          clearInterval(pollTimer);
          iniciarConWrapper(w);
        } else if (pollIniciar > 20) {
          clearInterval(pollTimer);
          console.warn('[KOI] shatokbIniciarKOI: wrapper nunca apareció tras polling');
        }
      }, 150);
    }
  };

  /**
   * Reset del estado de KOI para permitir re-inicialización.
   * Útil cuando shatokbScrollAKOI necesita forzar una segunda llamada.
   */
  window.shatokbResetKOI = function () {
    KOI_STATE.isReady = false;
    console.log('[KOI] Estado reseteado — próxima llamada a shatokbIniciarKOI creará el DOM de nuevo.');
  };

  /* ── Obtener contexto del localStorage como fallback ────── */
  /* ── Guardar historial en localStorage para KOI Cart ── */
  function guardarHistorialLocal () {
    try {
      // Guardar solo los últimos 20 mensajes para no sobrecargar
      const slice = KOI_STATE.historial.slice(-20);
      localStorage.setItem('shatokb_historial', JSON.stringify(slice));
    } catch(_) {}
  }

  function obtenerContextoLocal () {
    try {
      const raw = localStorage.getItem('shatokb_resultado');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  /* ══════════════════════════════════════════════════════════
     CONSTRUCCIÓN DEL DOM
     ══════════════════════════════════════════════════════════ */
  // ── Versión del archivo para diagnóstico en consola ──────────
  // (log de versión ya está en línea 19 — este era residuo de v3.1, eliminado)

  /**
   * Insertar KOI cuando #stk-routine-blurred esté disponible en el DOM.
   * Usa MutationObserver como método principal (instantáneo) y polling
   * como fallback.
   */
  function _insertarKOICuandoListo(wrapper) {
    // Estructura del quiz:
    //   #stk-reveal-section
    //     .stk-reveal-header     "YOUR PERSONALIZED ROUTINE"  ← se oculta al click
    //     #stk-blur-overlay      teaser "Open my analysis"    ← se oculta al click
    //     #stk-routine-blurred   productos borrosos           ← aparecen al click
    //     #shatokb-koi-wrapper   ← KOI va AQUÍ (al final, después de productos) ✅
    //
    // El usuario hace click → productos aparecen → scroll automático hasta KOI abajo.
    // IMPORTANTE: el quiz escribe inner.innerHTML UNA SOLA VEZ y luego
    // no lo toca más. Así que insertar dentro de #stk-reveal-section es seguro
    // siempre que lo hagamos DESPUÉS de que el quiz haya terminado de renderizar.

    function _doInsert() {
      const blurred = document.getElementById('stk-routine-blurred');
      if (blurred && blurred.parentNode) {
        // Insertar KOI DESPUÉS de #stk-routine-blurred (al final de los productos)
        blurred.parentNode.appendChild(wrapper);
        // Reducir espacio entre el botón "redo" y KOI
        wrapper.style.marginTop = '8px';
        console.log('[KOI] insertado después de #stk-routine-blurred (al final) ✅');
        return true;
      }
      return false;
    }

    // Intento inmediato
    if (_doInsert()) return;

    // MutationObserver: espera a que el quiz inserte #stk-routine-blurred
    // Usamos un pequeño delay (100ms) para asegurarnos de que innerHTML
    // terminó de escribir antes de insertar KOI
    var observer = new MutationObserver(function() {
      if (document.getElementById('stk-routine-blurred')) {
        observer.disconnect();
        setTimeout(function() { _doInsert(); }, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Timeout de seguridad a 8s
    setTimeout(function() {
      observer.disconnect();
      if (!_doInsert()) {
        var resultado = document.querySelector('.shatokb-resultado__inner')
                     || document.querySelector('#shatokb-resultado');
        if (resultado) {
          console.warn('[KOI] timeout — appending al contenedor raíz');
          resultado.appendChild(wrapper);
        }
      }
    }, 8000);
  }

  function crearDOM (intentoNum) {
    intentoNum = intentoNum || 0;

    // Evitar duplicados antes de buscar nada
    if (document.getElementById('shatokb-koi-wrapper')) {
      return document.getElementById('shatokb-koi-wrapper');
    }

    // El contenedor raíz del resultado debe existir
    const resultado = document.querySelector('.shatokb-resultado__inner')
                   || document.querySelector('.shatokb-resultado')
                   || document.querySelector('#shatokb-resultado');

    if (!resultado) {
      if (intentoNum < 15) {
        console.log('[KOI] crearDOM: esperando contenedor resultado (' + (intentoNum+1) + '/15)...');
        setTimeout(function() { crearDOM(intentoNum + 1); }, 200);
      } else {
        console.warn('[KOI] crearDOM: contenedor no apareció tras 3s — abortando.');
      }
      return null;
    }

    const wrapper = document.createElement('div');
    wrapper.id = 'shatokb-koi-wrapper';
    // Textos de UI localizados
    const idioma = detectarIdioma();
    const uiText = {
      es: { status: 'En línea · Especialista K-Beauty',    placeholder: 'Pregúntale algo a KOI...',     footer: '🔒 KOI ofrece orientación cosmética experta, no asesoramiento médico. Para condiciones dermatológicas diagnosticadas, consulta a un dermatólogo.' },
      en: { status: 'Online · Senior K-Beauty Specialist', placeholder: 'Ask KOI anything...',          footer: '🔒 KOI provides expert cosmetic guidance — not medical advice. For diagnosed skin conditions, consult a dermatologist.' },
      fr: { status: 'En ligne · Spécialiste K-Beauty',     placeholder: 'Posez une question à KOI...', footer: '🔒 KOI fournit des conseils cosmétiques experts — pas des avis médicaux. Pour des conditions dermatologiques, consultez un dermatologue.' },
      pt: { status: 'Online · Especialista K-Beauty',      placeholder: 'Pergunte algo à KOI...',      footer: '🔒 KOI oferece orientação cosmética especializada — não conselho médico. Para condições dermatológicas, consulte um dermatologista.' },
      de: { status: 'Online · K-Beauty-Spezialistin',      placeholder: 'Frage KOI etwas...',          footer: '🔒 KOI bietet kosmetische Fachberatung — keine medizinischen Ratschläge. Bei Hauterkrankungen einen Dermatologen aufsuchen.' },
      it: { status: 'Online · Specialista K-Beauty',       placeholder: 'Chiedi qualcosa a KOI...',    footer: '🔒 KOI fornisce consulenza cosmetica esperta — non consigli medici. Per condizioni dermatologiche, consulta un dermatologo.' },
    };
    const ui = uiText[idioma] || uiText['en'];

    wrapper.innerHTML = `
      <div class="koi-panel">

        <!-- Header -->
        <div class="koi-header">
          <div class="koi-header__avatar">🌸</div>
          <div class="koi-header__info">
            <div class="koi-header__name">
              KOI
              <span class="koi-header__status">
                <span class="koi-status-dot"></span>
                ${ui.status}
              </span>
            </div>
          </div>
          <div class="koi-header__badge">AI · shatokb</div>
        </div>

        <!-- Mini barra de carrito — fija debajo del header, siempre visible en mobile -->
        <div class="koi-mini-cart-bar" id="koi-mini-cart-bar">
          <div class="koi-mini-cart-bar__info">
            <span class="koi-mini-cart-bar__label" id="koi-mini-cart-label"></span>
            <div class="koi-mini-cart-bar__prices">
              <span class="koi-mini-cart-bar__total-orig" id="koi-mini-cart-total-orig"></span>
              <span class="koi-mini-cart-bar__total" id="koi-mini-cart-total"></span>
              <span class="koi-mini-cart-bar__off-badge">25% OFF</span>
            </div>
          </div>
          <button class="koi-mini-cart-bar__btn" id="koi-mini-cart-btn"></button>
        </div>

        <!-- Mensajes -->
        <div class="koi-messages" id="koi-messages"></div>

        <!-- Chips de respuesta rápida -->
        <div class="koi-chips" id="koi-chips"></div>

        <!-- Input -->
        <div class="koi-input-area">
          <textarea
            class="koi-input"
            id="koi-input"
            placeholder="${ui.placeholder}"
            rows="1"
            maxlength="500"
          ></textarea>
          <button class="koi-send-btn" id="koi-send-btn" title="Send">
            ➤
          </button>
        </div>

        <!-- Footer -->
        <div class="koi-footer">
          <p>${ui.footer}</p>
        </div>

      </div>
    `;

    // Insertar DESPUÉS de #stk-routine-blurred (al final de los productos).
    // Si aún no existe (el quiz no terminó de renderizar), MutationObserver
    // lo detectará en cuanto aparezca y lo añadirá al final.
    // Estructura final:
    //   stk-reveal-section
    //     .stk-reveal-header               ← se oculta al click
    //     #stk-blur-overlay (teaser)       ← se oculta al click
    //     div.stk-routine-blurred          ← productos sombreados (aparecen al click)
    //     #shatokb-koi-wrapper             ← KOI al final ✅ (scroll hasta aquí)
    _insertarKOICuandoListo(wrapper);

    // Vincular eventos
    vincularEventos();

    return wrapper;
  }

  /* ══════════════════════════════════════════════════════════
     EVENTOS
     ══════════════════════════════════════════════════════════ */
  function vincularEventos () {
    const input   = document.getElementById('koi-input');
    const sendBtn = document.getElementById('koi-send-btn');

    if (!input || !sendBtn) return;

    // Enviar con Enter (Shift+Enter = nueva línea)
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        enviarMensajeUsuario();
      }
    });

    // Auto-resize del textarea
    input.addEventListener('input', () => {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 120) + 'px';
    });

    // Botón enviar
    sendBtn.addEventListener('click', enviarMensajeUsuario);
  }

  /* ══════════════════════════════════════════════════════════
     CHIPS DE RESPUESTA RÁPIDA
     ══════════════════════════════════════════════════════════ */
  // Tipos de chips donde el input NO tiene sentido:
  // el usuario debe elegir una opción, no escribir libremente.
  const CHIPS_SIN_INPUT = new Set(['reveal', 'post_camara', 'post_vision']);

  function mostrarChips (tipo) {
    const container = document.getElementById('koi-chips');
    if (!container) return;

    // Ocultar/mostrar barra de input según el tipo de chips
    if (CHIPS_SIN_INPUT.has(tipo)) {
      setInputAreaVisible(false);
    } else {
      setInputAreaVisible(true);
      setInputHabilitado(true);
    }

    // Chips localizados según el idioma detectado del navegador
    const idioma     = detectarIdioma();
    const setIdioma  = KOI_CHIPS_I18N[idioma] || KOI_CHIPS_I18N['en'];
    const chips      = setIdioma[tipo] || setIdioma.bienvenida;
    container.innerHTML = '';

    chips.forEach(texto => {
      const btn = document.createElement('button');
      // post_vision = chip único de "muéstrame mis productos" — recibe clase CTA grande
      btn.className = tipo === 'post_vision'
        ? 'koi-chip koi-chip--reveal-cta'
        : 'koi-chip';
      btn.textContent = texto;
      btn.addEventListener('click', () => {
        container.innerHTML = '';
        // Al hacer clic en chip de acción → restaurar input
        setInputAreaVisible(true);
        setInputHabilitado(true);
        enviarDesdeChip(texto);
      });
      container.appendChild(btn);
    });
  }

  function ocultarChips () {
    const container = document.getElementById('koi-chips');
    if (container) container.innerHTML = '';
  }

  /* ══════════════════════════════════════════════════════════
     RENDERIZADO DE MENSAJES
     ══════════════════════════════════════════════════════════ */

  /**
   * Añade un mensaje al chat.
   * @param {string} rol - 'koi' | 'user'
   * @param {string} texto - contenido del mensaje
   * @param {boolean} esWelcome - aplica estilo de bienvenida
   * @returns {HTMLElement} - el elemento burbuja para streaming
   */
  function agregarMensaje (rol, texto, esWelcome = false) {
    const container = document.getElementById('koi-messages');
    if (!container) return null;

    const msg = document.createElement('div');
    msg.className = `koi-msg koi-msg--${rol}${esWelcome ? ' koi-msg--welcome' : ''}`;

    const avatarEmoji = rol === 'koi' ? '🌸' : '👤';
    const hora = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit'
    });

    msg.innerHTML = `
      <div class="koi-msg__avatar">${avatarEmoji}</div>
      <div class="koi-msg__bubble">
        <p class="koi-msg__text"></p>
        <span class="koi-msg__time">${hora}</span>
      </div>
    `;

    container.appendChild(msg);

    // Escribir texto (con efecto si es KOI, directo si es usuario)
    const textEl = msg.querySelector('.koi-msg__text');
    if (rol === 'user') {
      textEl.textContent = texto;
    } else {
      // El texto se llenará con streaming o efecto de escritura
      textEl.textContent = '';
    }

    scrollAlFinal();
    return textEl;
  }

  /**
   * Muestra el indicador de "escribiendo..."
   */
  function mostrarTyping () {
    const container = document.getElementById('koi-messages');
    if (!container || KOI_STATE.isTyping) return;

    KOI_STATE.isTyping = true;

    const typing = document.createElement('div');
    typing.className = 'koi-typing';
    typing.id = 'koi-typing-indicator';
    typing.innerHTML = `
      <div class="koi-msg__avatar">🌸</div>
      <div class="koi-typing__bubble">
        <span class="koi-typing__dot"></span>
        <span class="koi-typing__dot"></span>
        <span class="koi-typing__dot"></span>
      </div>
    `;

    container.appendChild(typing);
    scrollAlFinal();
  }

  function ocultarTyping () {
    const typing = document.getElementById('koi-typing-indicator');
    if (typing) typing.remove();
    KOI_STATE.isTyping = false;
  }

  function scrollAlFinal () {
    const container = document.getElementById('koi-messages');
    if (container) {
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 50);
    }
  }

  /* ══════════════════════════════════════════════════════════
     DETECTAR IDIOMA DEL NAVEGADOR
     Devuelve el idioma primario (ej: "es", "en", "fr", "pt")
     ══════════════════════════════════════════════════════════ */
  function detectarIdioma () {
    // Hardcoded 'en' — USA English audience only.
    // Previously auto-detected browser language, which caused Spanish UI
    // for users with Spanish browser settings. Fixed 2026-07.
    return 'en';
  }

  /* ══════════════════════════════════════════════════════════
     THE REVEAL — MENSAJE DE APERTURA
     KOI aparece ANTES de los productos con un insight
     poderoso y específico al perfil. No saluda — entra
     directo. Luego ofrece los dos chips de bifurcación.
     ══════════════════════════════════════════════════════════ */
  async function enviarMensajeKOI_proactivo () {
    // ── Ocultar barra de input mientras KOI escribe el mensaje
    //    de apertura. Se restaura cuando mostrarChips('post_camara')
    //    muestra los chips de bifurcación.
    setInputAreaVisible(false);
    setInputHabilitado(false);

    const idioma = detectarIdioma();

    // ── Mensaje único de apertura: propuesta de cámara directa ──
    // Sin repetir el perfil (ya visible en results page).
    // Crea curiosidad + coste mínimo percibido + promesa concreta.
    const mensajesCamara = {
      es: `Ya sé qué tipo de piel tienes. Lo que no sé es cómo está hoy. La cámara detecta lo que el quiz no puede — poros, zonas inflamadas, qué necesita atención ahora mismo. Son 10 segundos.`,
      en: `I know your skin type. What I don't know is how your skin looks today. The camera catches what the quiz can't — pores, inflamed zones, what needs attention right now. Ten seconds.`,
      fr: `Je sais quel type de peau tu as. Ce que je ne sais pas, c'est comment elle est aujourd'hui. La caméra détecte ce que le quiz ne peut pas — pores, zones enflammées, ce qui a besoin d'attention maintenant. Dix secondes.`,
      pt: `Já sei qual é o seu tipo de pele. O que não sei é como ela está hoje. A câmera detecta o que o quiz não pode — poros, zonas inflamadas, o que precisa de atenção agora. São 10 segundos.`,
      de: `Ich weiß, welchen Hauttyp du hast. Was ich nicht weiß, ist wie deine Haut heute aussieht. Die Kamera erkennt, was der Quiz nicht kann — Poren, entzündete Zonen, was jetzt Aufmerksamkeit braucht. Zehn Sekunden.`,
      it: `So che tipo di pelle hai. Quello che non so è com'è oggi. La fotocamera rileva ciò che il quiz non può — pori, zone infiammate, cosa ha bisogno di attenzione adesso. Dieci secondi.`,
    };

    const msg    = mensajesCamara[idioma] || mensajesCamara['en'];
    mostrarTyping();
    await new Promise(r => setTimeout(r, 900));
    ocultarTyping();
    const textEl = agregarMensaje('koi', '');
    if (textEl) await escribirConEfecto(textEl, msg);
    KOI_STATE.historial.push({ role: 'assistant', content: msg });
    guardarHistorialLocal();

    // Chips: analizar con cámara o ver productos directamente
    KOI_STATE.revealPhase = 'camara';
    setTimeout(() => mostrarChips('post_camara'), 400);
  }

  /* ══════════════════════════════════════════════════════════
     THE REVEAL — CHIPS HANDLER
     Intercepta los chips de bifurcación y ejecuta la lógica
     correspondiente (email gate o cámara).
     ══════════════════════════════════════════════════════════ */
  function esChipReveal (texto) {
    // Detecta si el chip es de bifurcación (reveal o cámara)
    const t = texto.toLowerCase();
    return t.includes('reveal') || t.includes('descubre') || t.includes('révéler') ||
           t.includes('revelar') || t.includes('enthüllen') || t.includes('rivela') ||
           t.includes('analyz') || t.includes('analiza') || t.includes('analyser') ||
           t.includes('analisar') || t.includes('analysier') || t.includes('analizza');
  }

  function esChipCamara (texto) {
    const t = texto.toLowerCase();
    return t.includes('📸') || t.includes('analyz') || t.includes('analiza') ||
           t.includes('analyser') || t.includes('analisar') || t.includes('analysier') ||
           t.includes('analizza') || t.includes('skin') && t.includes('first');
  }

  async function manejarChipReveal (texto) {
    const idioma = detectarIdioma();
    ocultarChips();

    // Ocultar barra de input mientras KOI escribe — el usuario
    // no puede interrumpir la fase insight/reveal
    setInputAreaVisible(false);
    setInputHabilitado(false);

    if (esChipCamara(texto)) {
      // Marcar fase → los chips post_camara no serán interceptados por esChipReveal
      KOI_STATE.revealPhase = 'camara';
      // ── Cámara — mensaje persuasivo con psicología de curiosidad + autoridad
      const mensajesCamara = {
        es: `Tus respuestas ya me dicen mucho — lo suficiente para armarte una rutina sólida.

Pero hay algo que no puedo ver solo con texto: la textura real de tu piel en este momento, cómo están tus poros, si hay enrojecimiento que quizás tú misma no has notado aún.

El análisis facial me permite ir **una capa más profundo**. No es obligatorio — tu rutina ya está lista de cualquier forma.

¿Quieres intentarlo? Son unos 10 segundos.`,
        en: `Your quiz answers already tell me a lot — enough to build you a solid routine.

But there's something I can't see from text alone: your skin's actual texture right now, how your pores look today, whether there's redness you might not have noticed yourself.

Facial analysis lets me go **one layer deeper**. It's completely optional — your routine is ready either way.

Want to try it? It takes about 10 seconds.`,
        fr: `Vos réponses m'en disent déjà beaucoup — assez pour construire une routine solide.

Mais il y a des choses que je ne peux pas voir avec du texte seul : la texture réelle de votre peau en ce moment, l'état de vos pores, une rougeur que vous n'auriez peut-être pas remarquée.

L'analyse faciale me permet d'aller **une couche plus loin**. C'est totalement optionnel — votre routine est prête de toute façon.

Vous voulez essayer ? Ça prend environ 10 secondes.`,
        pt: `Suas respostas já me dizem muito — o suficiente para montar uma rotina sólida.

Mas há algo que não consigo ver só com texto: a textura real da sua pele agora, como estão seus poros hoje, se há vermelhidão que talvez você mesma não tenha notado.

A análise facial me permite ir **uma camada mais fundo**. É completamente opcional — sua rotina já está pronta de qualquer jeito.

Quer tentar? Leva uns 10 segundos.`,
        de: `Deine Antworten sagen mir schon viel — genug, um eine solide Routine für dich zu erstellen.

Aber es gibt Dinge, die ich nur aus Text nicht sehen kann: die tatsächliche Textur deiner Haut gerade jetzt, wie deine Poren heute aussehen, ob es Rötungen gibt, die du vielleicht selbst nicht bemerkt hast.

Die Gesichtsanalyse lässt mich **eine Schicht tiefer** gehen. Völlig optional — deine Routine ist so oder so fertig.

Möchtest du es versuchen? Es dauert etwa 10 Sekunden.`,
        it: `Le tue risposte mi dicono già molto — abbastanza per costruire una routine solida.

Ma c'è qualcosa che non riesco a vedere solo dal testo: la texture reale della tua pelle in questo momento, come appaiono i tuoi pori oggi, se c'è arrossamento che forse non hai ancora notato.

L'analisi facciale mi permette di andare **un livello più in profondità**. È completamente opzionale — la tua routine è pronta in ogni caso.

Vuoi provare? Ci vogliono circa 10 secondi.`,
      };
      const msg    = mensajesCamara[idioma] || mensajesCamara['en'];
      const textEl = agregarMensaje('koi', '');
      if (textEl) await escribirConEfecto(textEl, msg);
      KOI_STATE.historial.push({ role: 'assistant', content: msg });
      guardarHistorialLocal();

      // Chips post-cámara: analizar o ver rutina directamente
      setTimeout(() => mostrarChips('post_camara'), 400);

    } else {
      // ── Pedir email ANTES del reveal (flujo original)
      await pedirEmailEnChat();
    }
  }

  /* ══════════════════════════════════════════════════════════
     THE REVEAL — WOW EMAIL GATE v7.0
     5 momentos cinematográficos en lugar de un formulario
     estático. KOI "prepara" el reporte, luego pide el email
     con una envelope card animada.
     ══════════════════════════════════════════════════════════ */

  /* ── Momento 1: KOI está preparando tu Skin Report ──────── */
  async function pedirEmailEnChat () {
    KOI_STATE.revealPhase = 'email';
    setInputHabilitado(false);
    setInputAreaVisible(false); // ocultar barra — el formulario de email la reemplaza

    // Pequeña pausa → typing visible
    mostrarTyping();
    await new Promise(r => setTimeout(r, 600));
    ocultarTyping();

    // Inyectar la tarjeta de preparación en el chat
    _inyectarPreparandoCard();
  }

  function _inyectarPreparandoCard () {
    const container = document.getElementById('koi-messages');
    if (!container || document.getElementById('koi-preparing-card')) return;

    const idioma = detectarIdioma();

    const LABELS = {
      es: {
        title:  'KOI está preparando tu Skin Report…',
        steps: [
          { icon: '🔍', text: 'Analizando tu perfil de piel' },
          { icon: '📋', text: 'Documentando tus productos' },
          { icon: '✍️', text: 'Escribiendo tu manual de uso' },
        ],
      },
      en: {
        title:  'KOI is preparing your Skin Report…',
        steps: [
          { icon: '🔍', text: 'Analyzing your skin profile' },
          { icon: '📋', text: 'Documenting your products' },
          { icon: '✍️', text: 'Writing your usage manual' },
        ],
      },
      fr: {
        title:  'KOI prépare votre Skin Report…',
        steps: [
          { icon: '🔍', text: 'Analyse de votre profil de peau' },
          { icon: '📋', text: 'Documentation de vos produits' },
          { icon: '✍️', text: 'Rédaction de votre manuel' },
        ],
      },
      pt: {
        title:  'KOI está preparando seu Skin Report…',
        steps: [
          { icon: '🔍', text: 'Analisando seu perfil de pele' },
          { icon: '📋', text: 'Documentando seus produtos' },
          { icon: '✍️', text: 'Escrevendo seu manual de uso' },
        ],
      },
      de: {
        title:  'KOI bereitet deinen Skin Report vor…',
        steps: [
          { icon: '🔍', text: 'Analyse deines Hautprofils' },
          { icon: '📋', text: 'Dokumentation deiner Produkte' },
          { icon: '✍️', text: 'Schreiben deines Anwendungshandbuchs' },
        ],
      },
      it: {
        title:  'KOI sta preparando il tuo Skin Report…',
        steps: [
          { icon: '🔍', text: 'Analisi del tuo profilo pelle' },
          { icon: '📋', text: 'Documentazione dei tuoi prodotti' },
          { icon: '✍️', text: 'Scrittura del tuo manuale di utilizzo' },
        ],
      },
    };
    const lbl = LABELS[idioma] || LABELS['en'];

    const card = document.createElement('div');
    card.id        = 'koi-preparing-card';
    card.className = 'koi-preparing-card';
    card.innerHTML = `
      <div class="koi-preparing__title">${lbl.title}</div>
      <ul class="koi-preparing__steps">
        ${lbl.steps.map((s, i) => `
          <li class="koi-preparing__step" data-idx="${i}">
            <span class="koi-preparing__step-icon">${s.icon}</span>
            <span class="koi-preparing__step-text">${s.text}</span>
            <span class="koi-preparing__step-check">✓</span>
          </li>`).join('')}
      </ul>
    `;

    container.appendChild(card);
    scrollAlFinal();

    // Animar pasos uno a uno (800ms c/u)
    const stepEls = card.querySelectorAll('.koi-preparing__step');
    let delay = 200;
    stepEls.forEach((el, i) => {
      setTimeout(() => {
        el.classList.add('koi-preparing__step--active');
        scrollAlFinal();
      }, delay);
      delay += 800;
      setTimeout(() => {
        el.classList.add('koi-preparing__step--done');
        scrollAlFinal();
      }, delay);
      delay += 200;
    });

    // Cuando todos terminan → morph a envelope card (~3400ms)
    const totalDelay = 200 + (3 * 800) + (3 * 200) + 400;
    setTimeout(() => _morfar_a_envelope(card), totalDelay);
  }

  /* ── Momento 2: Envelope card con checkmarks + email input ─ */
  function _morfar_a_envelope (preparandoCard) {
    preparandoCard.classList.add('koi-preparing-card--out');
    setTimeout(() => {
      if (preparandoCard.parentNode) preparandoCard.remove();
      _inyectarFocusMode();   // ← reemplaza _inyectarEnvelopeCard
    }, 350);
  }

  /* ── Focus Mode — overlay sobre TODO el panel + card centrada ──
     El overlay y la card se insertan en .koi-panel (no en el scroll
     de mensajes), así oscurecen header + messages + chips + input.
     El mini-cart-bar queda visible por encima (z-index:40).         */
  function _inyectarFocusMode () {
    const panel   = document.querySelector('#shatokb-koi-wrapper .koi-panel');
    const wrapper = document.getElementById('shatokb-koi-wrapper');
    if (!panel || !wrapper || document.getElementById('koi-focus-overlay')) return;

    const idioma = detectarIdioma();

    const LABELS = {
      es: {
        title:       '📩 Tu Skin Report está listo',
        checks: [
          'Rutina AM/PM personalizada',
          'Por qué cada producto fue elegido',
          'Orden de aplicación + tips',
          'Guía de ingredientes clave',
        ],
        subtitle:    '¿A dónde te lo envío?',
        placeholder: 'tu@email.com',
        btn:         'Enviar mi rutina →',
        note:        '🔒 Solo para enviarte tu rutina. Sin spam.',
        skip:        '🛒 Prefiero ver mi rutina sin dejar email',
      },
      en: {
        title:       '📩 Your Skin Report is ready',
        checks: [
          'Personalized AM/PM routine',
          'Why each product was chosen',
          'Application order + tips',
          'Key ingredients guide',
        ],
        subtitle:    'Where should I send it?',
        placeholder: 'you@email.com',
        btn:         'Send my routine →',
        note:        '🔒 Only to send you your routine. No spam.',
        skip:        '🛒 See my routine without email',
      },
      fr: {
        title:       '📩 Votre Skin Report est prêt',
        checks: [
          'Routine AM/PM personnalisée',
          'Pourquoi chaque produit a été choisi',
          'Ordre d\'application + conseils',
          'Guide des ingrédients clés',
        ],
        subtitle:    'Où dois-je vous l\'envoyer ?',
        placeholder: 'vous@email.com',
        btn:         'Envoyer ma routine →',
        note:        '🔒 Uniquement pour votre routine.',
        skip:        '🛒 Voir ma routine sans email',
      },
      pt: {
        title:       '📩 Seu Skin Report está pronto',
        checks: [
          'Rotina AM/PM personalizada',
          'Por que cada produto foi escolhido',
          'Ordem de aplicação + dicas',
          'Guia de ingredientes principais',
        ],
        subtitle:    'Para onde envio?',
        placeholder: 'voce@email.com',
        btn:         'Enviar minha rotina →',
        note:        '🔒 Apenas para sua rotina. Sem spam.',
        skip:        '🛒 Ver minha rotina sem email',
      },
      de: {
        title:       '📩 Dein Skin Report ist fertig',
        checks: [
          'Personalisierte AM/PM-Routine',
          'Warum jedes Produkt gewählt wurde',
          'Anwendungsreihenfolge + Tipps',
          'Leitfaden für wichtige Inhaltsstoffe',
        ],
        subtitle:    'Wohin soll ich es schicken?',
        placeholder: 'du@email.com',
        btn:         'Routine senden →',
        note:        '🔒 Nur für deine Routine. Kein Spam.',
        skip:        '🛒 Routine ohne E-Mail ansehen',
      },
      it: {
        title:       '📩 Il tuo Skin Report è pronto',
        checks: [
          'Routine AM/PM personalizzata',
          'Perché ogni prodotto è stato scelto',
          'Ordine di applicazione + consigli',
          'Guida agli ingredienti chiave',
        ],
        subtitle:    'Dove te lo mando?',
        placeholder: 'tu@email.com',
        btn:         'Invia la mia routine →',
        note:        '🔒 Solo per inviarti la tua routine.',
        skip:        '🛒 Vedi la routine senza email',
      },
    };
    const lbl = LABELS[idioma] || LABELS['en'];

    // OVERLAY: va dentro de .koi-panel (position:absolute inset:0)
    // → cubre TODA la superficie del panel (header + mini-cart + mensajes)
    // → z-index:10 supera el header (z-index:1) y el mini-cart-bar (z-index:2)
    // → overflow:hidden del .koi-panel atrapa el overlay → el chat no puede scrollear
    // CARD: va en document.body (position:fixed centrada en viewport)
    // → escapa del transform:matrix() del wrapper
    // → z-index:2147483647 → siempre encima de todo
    const styleTag = document.createElement('style');
    styleTag.id = 'koi-focus-style';
    styleTag.textContent =
      '#koi-focus-overlay{position:absolute!important;inset:0!important;top:0!important;left:0!important;width:100%!important;height:100%!important;background:rgba(236,149,184,0.18)!important;z-index:10!important;pointer-events:auto!important;display:block!important;border-radius:inherit!important;}' +
      '#koi-focus-card{position:fixed!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)!important;z-index:2147483647!important;pointer-events:auto!important;display:flex!important;flex-direction:column!important;width:calc(100vw - 40px)!important;max-width:440px!important;background:#1c181a!important;border:1px solid rgba(236,149,184,0.35)!important;border-radius:18px!important;box-shadow:0 24px 64px rgba(0,0,0,0.75)!important;padding:26px 26px 20px!important;gap:16px!important;box-sizing:border-box!important;}' +
      '#koi-page-backdrop{position:fixed!important;inset:0!important;top:0!important;left:0!important;width:100vw!important;height:100vh!important;background:rgba(236,149,184,0.18)!important;z-index:2147483640!important;pointer-events:auto!important;}';
    document.head.appendChild(styleTag);

    // ── Bloquear scroll: página exterior + panel del chat ────────────────────
    // Evita que el usuario scrollee el fondo de Shopify o el chat.
    // Se bloquea tanto <html> como <body> porque distintos temas Shopify
    // usan uno u otro como scroll root.
    const _bodyOverflowPrev  = document.body.style.overflow;
    const _htmlOverflowPrev  = document.documentElement.style.overflow;
    const _panelOverflowPrev = panel.style.overflow;
    document.body.style.overflow            = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    panel.style.overflow                    = 'hidden';

    // ── Backdrop de página completa (position:fixed cubre todo el viewport) ──
    const backdrop = document.createElement('div');
    backdrop.id = 'koi-page-backdrop';
    document.body.appendChild(backdrop);

    // ── Overlay dentro del .koi-panel — color vino + blur sobre TODO el panel ──
    // position:absolute inset:0 cubre cada píxel del panel (header, mini-cart,
    // mensajes). El color #3e2b32 con backdrop-filter:blur desenfoca el contenido
    // del panel visible detrás de la card. z-index:10 supera header(1) y cart(2).
    const overlay = document.createElement('div');
    overlay.id = 'koi-focus-overlay';
    // ── Color DIRECTO en el elemento via style inline ─────────────────────────
    // El diagnóstico confirmó: el CSS del styleTag no llega (Shopify cachea el JS
    // viejo). Usar style.setProperty con !important directamente en el elemento
    // es la única forma garantizada de superar cualquier CSS existente.
    overlay.style.setProperty('position',   'absolute',                   'important');
    overlay.style.setProperty('inset',      '0',                          'important');
    overlay.style.setProperty('width',      '100%',                       'important');
    overlay.style.setProperty('height',     '100%',                       'important');
    overlay.style.setProperty('background', 'rgba(236,149,184,0.25)',     'important');
    overlay.style.setProperty('z-index',    '10',                         'important');
    overlay.style.setProperty('pointer-events', 'auto',                   'important');
    overlay.style.setProperty('display',    'block',                      'important');
    panel.appendChild(overlay);

    // ── Color vino en .koi-panel directamente ─────────────────────────────
    // El panel tiene bg rgb(28,24,26). Lo cambiamos a vino para que el overlay
    // semitransparente encima se vea diferente al negro.
    panel.style.setProperty('background', '#3e2b32', 'important');
    panel.style.setProperty('background-color', '#3e2b32', 'important');

    const messages = document.getElementById('koi-messages');
    const header   = document.querySelector('#shatokb-koi-wrapper .koi-header');

    // Card → document.body (no al wrapper) para escapar del transform:matrix()
    const card = document.createElement('div');
    card.id        = 'koi-focus-card';
    card.className = 'koi-focus-card';
    card.innerHTML = `
      <div class="koi-focus-card__koi">
        <div class="koi-focus-card__avatar">🌸</div>
        <div class="koi-focus-card__name">KOI</div>
      </div>
      <div class="koi-focus-card__bubble">
        <p class="koi-focus-card__headline">${lbl.title}</p>
        <ul class="koi-envelope__checks" style="list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;">
          ${lbl.checks.map(c => `<li class="koi-envelope__check"><span class="koi-envelope__check-icon">✓</span><span>${c}</span></li>`).join('')}
        </ul>
        <p class="koi-focus-card__ask">${lbl.subtitle}</p>
      </div>
      <div class="koi-focus-card__form">
        <input type="email" id="koi-focus-email-input" class="koi-focus-card__input" placeholder="${lbl.placeholder}" autocomplete="email" inputmode="email"/>
        <button class="koi-focus-card__btn" id="koi-focus-email-btn">${lbl.btn}</button>
      </div>
      <div class="koi-focus-card__footer">
        <span class="koi-focus-card__note">${lbl.note}</span>
        <button class="koi-focus-card__skip" id="koi-focus-skip">${lbl.skip}</button>
      </div>
    `;
    document.body.appendChild(card); // body, no wrapper — escapa del transform:matrix()

    const checkEls = card.querySelectorAll('.koi-envelope__check');
    checkEls.forEach((el, i) => {
      setTimeout(() => el.classList.add('koi-envelope__check--visible'), 200 + i * 180);
    });

    setTimeout(() => {
      const inp = document.getElementById('koi-focus-email-input');
      if (inp && window.innerWidth > 768) inp.focus();
    }, 200 + 4 * 180 + 150);

    function _cerrarFocusMode () {
      const o  = document.getElementById('koi-focus-overlay');
      const c  = document.getElementById('koi-focus-card');
      const bg = document.getElementById('koi-page-backdrop');
      if (o)  o.remove();
      if (c)  c.remove();
      if (bg) bg.remove();
      const st = document.getElementById('koi-focus-style');
      if (st) st.remove();
      // Restaurar panel, mensajes y header
      panel.style.removeProperty('background');
      panel.style.removeProperty('background-color');
      if (messages) messages.style.removeProperty('background');
      if (header)   header.style.removeProperty('background');
      // Restaurar scroll en body, html y panel
      document.body.style.overflow            = _bodyOverflowPrev;
      document.documentElement.style.overflow = _htmlOverflowPrev;
      panel.style.overflow                    = _panelOverflowPrev;
    }

    async function _confirmarFocusEmail () {
      const inp = document.getElementById('koi-focus-email-input');
      const btn = document.getElementById('koi-focus-email-btn');
      if (!inp) return;
      const email = inp.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        inp.classList.add('koi-focus-card__input--error');
        setTimeout(() => inp.classList.remove('koi-focus-card__input--error'), 800);
        return;
      }
      if (btn) { btn.disabled = true; btn.textContent = '⏳'; }
      KOI_STATE.emailCaptured = email;
      try { localStorage.setItem('shatokb_email', email); } catch (_) {}
      shatokbEnviarEmailShopify(email);
      agregarMensaje('user', email);
      setInputHabilitado(true);
      _cerrarFocusMode();
      await revelarRutinaConKOI(email);
    }

    function _skipFocusEmail () {
      _cerrarFocusMode();
      setInputHabilitado(true);
      revelarRutinaConKOI('');
    }

    const inp = document.getElementById('koi-focus-email-input');
    const btn = document.getElementById('koi-focus-email-btn');
    const skip = document.getElementById('koi-focus-skip');
    if (btn)  btn.addEventListener('click',  _confirmarFocusEmail);
    if (inp)  inp.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); _confirmarFocusEmail(); } });
    if (skip) skip.addEventListener('click', _skipFocusEmail);
  }

  function _inyectarEnvelopeCard () {
    const container = document.getElementById('koi-messages');
    if (!container || document.getElementById('koi-email-gate')) return;

    const idioma = detectarIdioma();

    const LABELS = {
      es: {
        title:       '📩 Tu Skin Report está listo',
        checks: [
          'Rutina AM/PM personalizada',
          'Por qué cada producto fue elegido',
          'Orden de aplicación + tips',
          'Guía de ingredientes clave',
        ],
        subtitle:    '¿A dónde te lo envío?',
        placeholder: 'tu@email.com',
        btn:         'Enviar mi rutina →',
        note:        '🔒 Solo para enviarte tu rutina. Sin spam.',
      },
      en: {
        title:       '📩 Your Skin Report is ready',
        checks: [
          'Personalized AM/PM routine',
          'Why each product was chosen',
          'Application order + tips',
          'Key ingredients guide',
        ],
        subtitle:    'Where should I send it?',
        placeholder: 'you@email.com',
        btn:         'Send my routine →',
        note:        '🔒 Only to send you your routine. No spam.',
      },
      fr: {
        title:       '📩 Votre Skin Report est prêt',
        checks: [
          'Routine AM/PM personnalisée',
          'Pourquoi chaque produit a été choisi',
          'Ordre d\'application + conseils',
          'Guide des ingrédients clés',
        ],
        subtitle:    'Où dois-je vous l\'envoyer ?',
        placeholder: 'vous@email.com',
        btn:         'Envoyer ma routine →',
        note:        '🔒 Uniquement pour votre routine.',
      },
      pt: {
        title:       '📩 Seu Skin Report está pronto',
        checks: [
          'Rotina AM/PM personalizada',
          'Por que cada produto foi escolhido',
          'Ordem de aplicação + dicas',
          'Guia de ingredientes principais',
        ],
        subtitle:    'Para onde envio?',
        placeholder: 'voce@email.com',
        btn:         'Enviar minha rotina →',
        note:        '🔒 Apenas para sua rotina. Sem spam.',
      },
      de: {
        title:       '📩 Dein Skin Report ist fertig',
        checks: [
          'Personalisierte AM/PM-Routine',
          'Warum jedes Produkt gewählt wurde',
          'Anwendungsreihenfolge + Tipps',
          'Leitfaden für wichtige Inhaltsstoffe',
        ],
        subtitle:    'Wohin soll ich es schicken?',
        placeholder: 'du@email.com',
        btn:         'Routine senden →',
        note:        '🔒 Nur für deine Routine. Kein Spam.',
      },
      it: {
        title:       '📩 Il tuo Skin Report è pronto',
        checks: [
          'Routine AM/PM personalizzata',
          'Perché ogni prodotto è stato scelto',
          'Ordine di applicazione + consigli',
          'Guida agli ingredienti chiave',
        ],
        subtitle:    'Dove te lo mando?',
        placeholder: 'tu@email.com',
        btn:         'Invia la mia routine →',
        note:        '🔒 Solo per inviarti la tua routine.',
      },
    };
    const lbl = LABELS[idioma] || LABELS['en'];

    const gate = document.createElement('div');
    gate.id        = 'koi-email-gate';
    gate.className = 'koi-email-gate koi-email-gate--wow';
    gate.innerHTML = `
      <div class="koi-envelope__title">${lbl.title}</div>
      <ul class="koi-envelope__checks">
        ${lbl.checks.map(c => `<li class="koi-envelope__check"><span class="koi-envelope__check-icon">✓</span><span>${c}</span></li>`).join('')}
      </ul>
      <div class="koi-envelope__subtitle">${lbl.subtitle}</div>
      <input
        type="email"
        id="koi-email-input"
        class="koi-email-input"
        placeholder="${lbl.placeholder}"
        autocomplete="email"
        inputmode="email"
      />
      <button class="koi-email-btn" id="koi-email-btn">${lbl.btn}</button>
      <p class="koi-email-note">${lbl.note}</p>
    `;

    container.appendChild(gate);
    scrollAlFinal();

    // Animar checkmarks con stagger 200ms
    const checkEls = gate.querySelectorAll('.koi-envelope__check');
    checkEls.forEach((el, i) => {
      setTimeout(() => el.classList.add('koi-envelope__check--visible'), 150 + i * 200);
    });

    // Foco automático en desktop (después del stagger)
    setTimeout(() => {
      const inp = document.getElementById('koi-email-input');
      if (inp && window.innerWidth > 768) inp.focus();
    }, 150 + 4 * 200 + 100);

    // Eventos
    const inp = document.getElementById('koi-email-input');
    const btn = document.getElementById('koi-email-btn');
    if (btn) btn.addEventListener('click', confirmarEmail);
    if (inp) inp.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); confirmarEmail(); }
    });
  }

  /* ── Confirmar email + Momento 3: cart CTA inmediato ───── */
  async function confirmarEmail () {
    const inp = document.getElementById('koi-email-input');
    const gate = document.getElementById('koi-email-gate');
    if (!inp || !gate) return;

    const email = inp.value.trim();
    const idioma = detectarIdioma();

    // Validación simple
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      inp.classList.add('koi-email-input--error');
      setTimeout(() => inp.classList.remove('koi-email-input--error'), 800);
      return;
    }

    // Guardar email
    KOI_STATE.emailCaptured = email;

    // Quitar el gate con animación
    gate.classList.add('koi-email-gate--confirmed');
    setTimeout(() => { if (gate.parentNode) gate.remove(); }, 400);

    // Mostrar email del usuario como mensaje en el chat
    agregarMensaje('user', email);

    // Re-habilitar input
    setInputHabilitado(true);

    // Guardar email en localStorage
    try { localStorage.setItem('shatokb_email', email); } catch (_) {}

    // Enviar email a Shopify /contact (silencioso, sin redirigir)
    shatokbEnviarEmailShopify(email);

    // KOI confirma y revela (incluye Momentos 3, 4, 5)
    await revelarRutinaConKOI(email);
  }



  function shatokbEnviarEmailShopify (email) {
    // POST silencioso al endpoint de Shopify /contact
    // Mismo mecanismo que el email gate original del quiz
    const ctx  = KOI_STATE.contexto;
    const body = new URLSearchParams({
      'form_type':   'customer',
      'utf8':        '✓',
      'contact[email]': email,
      'contact[body]':  `[KOI The Reveal] Perfil: ${ctx?.perfil?.nombre || ''} | Productos: ${(ctx?.productos || []).map(p => p.nombre).join(', ')}`,
      'contact[tags]':  'koi-lead, quiz-completed',
    });

    fetch('/contact', {
      method:  'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:    body.toString(),
    }).catch(() => {}); // silencioso — no interrumpir la experiencia
  }

  /* ══════════════════════════════════════════════════════════
     SKIN REPORT — Guarda el reporte en la tabla API y
     dispara el evento en Klaviyo via el Worker.
     Se llama silenciosamente al capturar el email en el
     interceptor del carrito.
     ══════════════════════════════════════════════════════════ */
  // ── Exponer globalmente para que shatokb-quiz.js pueda llamarla desde Gate 1 ──
  // El quiz.js no tiene acceso al closure de koi-chat.js, así que necesitamos
  // este puente. Se asigna aquí (dentro del closure) para que tenga acceso a
  // KOI_CONFIG, KOI_STATE, y todas las funciones helper internas.
  // Añadido 2026-07-27.
  window.shatokbEnviarSkinReportGlobal = async function(email) {
    // Sincronizar email en KOI_STATE para que shatokbInterceptarCarrito lo encuentre
    if (email && !KOI_STATE.emailCaptured) {
      KOI_STATE.emailCaptured = email;
    }
    return enviarSkinReport(email, { sendKlaviyoDirect: true });
  };

  async function enviarSkinReport (email, options = {}) {
    const ctx    = KOI_STATE.contexto;
    if (!ctx || !email) return;

    // ── Mapa canónico ID → nombre bonito (SIEMPRE tiene prioridad sobre ctx.perfil.nombre) ──
    const PERFIL_NOMBRES = {
      grasa_acne:       'Oil Balance & Clarity',
      grasa_poros:      'Pore Refinement',
      mixta_general:    'Zone Balance',
      mixta_manchas:    'Balance & Brighten',
      seca_hidratacion: 'Hydration Restore',
      seca_antiaging:   'Age Defense',
      sensible_rojeces: 'Calm & Repair',
      barrera_daniada:  'Barrier Recovery',
      general_glow:     'Glass Skin Glow',
    };

    const perfilId     = ctx.perfil?.id     || '';
    // SIEMPRE usar el mapa de nombres bonitos primero — ctx.perfil.nombre puede
    // contener el título largo ('The Deep Hydration Protocol') o incluso el ID raw.
    const perfilNombre = PERFIL_NOMBRES[perfilId] || ctx.perfil?.nombre || perfilId || '';

    // ══════════════════════════════════════════════════════════════
    // PRODUCTOS EN VIVO — fuente de verdad: window.shatokbState.selectedProducts
    // ══════════════════════════════════════════════════════════════
    let productosSeleccionados = [];
    const liveState   = window.shatokbState;
    const liveCatalog = window.SHATOKB_CATALOGO;

    if (liveState?.selectedProducts && liveCatalog?.length > 0) {
      // Fuente 1: selectedProducts + catálogo (mejor fuente cuando el catálogo está disponible)
      const ctxProds  = (window.SHATOKB_RESULTADO?.productos || ctx.productos || []);
      const pasosMeta = {};
      ctxProds.forEach((p, i) => {
        pasosMeta[i] = { paso: p.paso || '', momento: p.momento || 'ambos', razon: p.razon || '' };
      });

      productosSeleccionados = Object.entries(liveState.selectedProducts)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([stepIdx, prodId]) => {
          const prod = liveCatalog.find(c => c.id === prodId);
          if (!prod) return null;
          const meta       = pasosMeta[stepIdx] || {};
          const momentoRaw = prod.momento || meta.momento || 'both';
          const momento    = momentoRaw === 'both' ? 'ambos'
                           : momentoRaw === 'am'   ? 'am'
                           : momentoRaw === 'pm'   ? 'pm'
                           : 'ambos';
          const imagen = (prod.imagen && prod.imagen.startsWith('http')) ? prod.imagen : '';
          return {
            nombre:  prod.nombre  || '',
            precio:  prod.precio  || '',
            paso:    meta.paso    || prod.categoria || '',
            id:      prod.id      || '',
            handle:  prod.handle  || prod.id || '',
            momento,
            razon:   meta.razon   || prod.desc || '',
            imagen,
            url:     prod.handle ? `https://shatokb.com/products/${prod.handle}` : '',
          };
        })
        .filter(Boolean);
      console.log('[KOI] enviarSkinReport — usando selectedProducts+catálogo:', productosSeleccionados.length, 'productos');
    } else {
      // Fuente 2: ctx.productos (siempre disponible — productos que el quiz mostró)
      productosSeleccionados = (ctx.productos || []).map(p => {
        let imagen = (p.imagen && p.imagen.startsWith('http')) ? p.imagen : '';
        if (!imagen && liveCatalog?.length > 0) {
          const cat = liveCatalog.find(c => c.id === p.id || c.handle === p.handle);
          if (cat?.imagen && cat.imagen.startsWith('http')) imagen = cat.imagen;
        }
        const momento = (p.momento === 'both' || !p.momento) ? 'ambos'
                      : (p.momento === 'am') ? 'am'
                      : (p.momento === 'pm') ? 'pm'
                      : (p.momento || 'ambos');
        return {
          nombre:  p.nombre  || '',
          precio:  p.precio  || '',
          paso:    p.paso    || '',
          id:      p.id      || '',
          handle:  p.handle  || '',
          momento,
          razon:   p.razon   || '',
          imagen,
          url:     p.handle ? `https://shatokb.com/products/${p.handle}` : '',
        };
      }).filter(p => p.nombre);
      console.log('[KOI] enviarSkinReport — usando ctx.productos:', productosSeleccionados.length, 'productos');
    }

    const totalCarritoVivo = productosSeleccionados.reduce((sum, p) => {
      const num = parseFloat(String(p.precio).replace(/[^0-9.]/g, '')) || 0;
      return sum + num;
    }, 0);

    const isAM = p => p.momento === 'am' || p.momento === 'ambos' || !p.momento;
    const isPM = p => p.momento === 'pm' || p.momento === 'ambos';
    const rutinaAMvivo = productosSeleccionados.filter(isAM).map(p => p.nombre).filter(Boolean);
    const rutinaPMvivo = productosSeleccionados.filter(isPM).map(p => p.nombre).filter(Boolean);

    const reportData = {
      email,
      perfil: {
        id:          perfilId,
        nombre:      perfilNombre,
        descripcion: ctx.perfil?.descripcion || '',
        tags:        ctx.perfil?.tags        || [],
      },
      rutinaAM: rutinaAMvivo.length > 0 ? rutinaAMvivo : (ctx.rutinaAM || []),
      rutinaPM: rutinaPMvivo.length > 0 ? rutinaPMvivo : (ctx.rutinaPM || []),
      productosSeleccionados,
      totalCarrito: totalCarritoVivo || ctx.totalCarrito || 0,
      presupuesto:           ctx.presupuesto  || '',
      experiencia:           ctx.experiencia  || '',
      idioma:                detectarIdioma(),
      createdAt:             Date.now(),
      // Vision analysis (GPT-4o foto) — incluye scores, zonas, mensaje_koi, ingredientes
      visionAnalysis:        KOI_STATE.visionResult || null,
      // URL del carrito de Shopify para el botón CTA del Skin Report
      cartUrl:               'https://shatokb.com/cart',
    };

    // ══════════════════════════════════════════════════════════════
    // ARQUITECTURA FINAL (Jun 2026):
    //
    //  El Worker de Cloudflare es el único storage del reporte.
    //  Guarda en Cloudflare KV (namespace SKIN_REPORTS) y envía
    //  evento Klaviyo en un solo POST.
    //
    //  shatokb-skin-report.js lee el reporte via GET /report/:token
    //  al Worker — que responde desde KV.
    //
    //  Flujo: Shopify → POST Worker /report → KV + Klaviyo ✅
    //         Shopify → GET Worker /report/:token → KV ✅
    // ══════════════════════════════════════════════════════════════

    // POST al Worker — guarda en KV y retorna token.
    // Esta función ahora es async y RETORNA el token para que confirmarEmailCarrito()
    // pueda hacer await real sin setTimeout fijo.
    const sendKlaviyoDirect = options?.sendKlaviyoDirect === true;

    try {
      const res = await fetch(KOI_CONFIG.reportUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          email,
          reportData,
          siteUrl:       KOI_CONFIG.siteUrl || 'https://shatokb.com',
          perfil_id:     perfilId,
          perfil_nombre: perfilNombre,
          perfil_desc:   reportData.perfil?.descripcion || '',
          total_carrito: reportData.totalCarrito        || 0,
          idioma:        reportData.idioma              || 'en',
          send_klaviyo:  sendKlaviyoDirect,
        }),
      });

      if (!res.ok) {
        console.warn('[KOI] Worker /report HTTP error:', res.status);
        return null;
      }

      const data = await res.json();

      if (data.ok && data.token) {
        KOI_STATE.reportUrl = data.reportUrl;
        // ── Guardar token en las 3 fuentes — triple redundancia ────────────
        window.KOI_STATE_REPORT_TOKEN = data.token;
        try { localStorage.setItem('shatokb_report_token', data.token); } catch (_) {}
        // Tercer backup: sessionStorage (sobrevive soft navigations)
        try { sessionStorage.setItem('shatokb_report_token', data.token); } catch (_) {}

        console.log('%c[KOI] ✅ Token generado y guardado:', 'color:#22c55e;font-weight:bold', data.token.slice(0, 8) + '…');
        console.log('[KOI] KV saved:', data.kv_saved, '| Klaviyo deferred→PATCH:', data.klaviyo?.deferred ?? true);
        return data.token;    // ← la clave: retornamos el token al caller
      } else {
        console.warn('[KOI] Worker /report respuesta inesperada:', data);
        return null;
      }
    } catch (e) {
      console.warn('[KOI] Worker /report network error:', e.message);
      return null;
    }
  }

  // Flag booleano síncrono — guard más rápido que el string revealPhase
  // Evita race condition si revelarRutinaConKOI se llama dos veces antes
  // de que el primer await haya terminado.
  let _revelarEnCurso = false;

  async function revelarRutinaConKOI (email) {
    // Doble guard: booleano síncrono + string de fase
    if (_revelarEnCurso) return;
    _revelarEnCurso = true;
    if (KOI_STATE.revealPhase === 'revealed') {
      _revelarEnCurso = false;
      return;
    }
    KOI_STATE.revealPhase = 'revealed';

    const idioma       = detectarIdioma();
    const perfilNombre = KOI_STATE.contexto?.perfil?.nombre || '';

    // ── Momento 3a: Mensaje de reveal ────────────────────────
    // Estructura:
    //   CON FOTO:  intro → mensaje_reveal del Worker (diagnóstico clínico GPT-4o) → cierre → instrucciones
    //   SIN FOTO:  diagnóstico dinámico desde quiz (tono dermatológico) → cierre → instrucciones

    // ★ LÓGICA CENTRAL: usa mensaje_reveal del análisis visual si existe
    const visionReveal = KOI_STATE.visionResult?.mensaje_reveal;

    // ── Leer respuestas del quiz para construir diagnóstico sin foto ──
    const respuestas  = KOI_STATE.contexto?.respuestas || {};
    const perfilId    = KOI_STATE.contexto?.perfil?.id || '';
    const tipoPiel    = respuestas.tipo_piel    || '';
    const sensib      = respuestas.sensibilidad || '';
    const preocupMain = Array.isArray(respuestas.preocupacion)
      ? respuestas.preocupacion[0] || respuestas.preocupacion
      : (respuestas.preocupacion || '');
    const preocupAll  = Array.isArray(respuestas.preocupacion)
      ? respuestas.preocupacion
      : (respuestas.preocupacion ? [respuestas.preocupacion] : []);
    const objetivo    = Array.isArray(respuestas.objetivo)
      ? respuestas.objetivo[0] || ''
      : (respuestas.objetivo || '');

    // ── Función: construir diagnóstico clínico desde perfil del quiz ──
    // Devuelve un párrafo de diagnóstico dermatológico según el perfil asignado.
    // Tono: especialista que conoce esta piel, no genérico.
    function _construirDiagnosticoQuiz (perfilId, tipoPiel, sensib, preocupAll, idioma) {
      // Diagnósticos por perfil — tono especialista accesible:
      // sabe mucho, explica bien, nunca abruma con jerga.
      // Cada texto nombra qué pasa, por qué pasa, y qué significa para esta piel.
      const dx = {

        grasa_acne: {
          es: `Tu piel produce más sebo del que necesita — y ese exceso es el que tapa los poros, genera puntos negros y alimenta los brotes. Pero hay algo que mucha gente no sabe: la piel grasa con tendencia al acné suele estar deshidratada por dentro al mismo tiempo. Produce grasa como mecanismo de defensa porque le falta agua. Es por eso que los productos que la "secan" la empeoran: le quitan lo poco que tiene y la obligan a producir todavía más sebo. Lo que necesitas no es eliminar la grasa — es equilibrarla.`,
          en: `Your skin produces more oil than it needs — and that excess is what clogs pores, creates blackheads, and fuels breakouts. But there's something many people don't know: oily acne-prone skin is often dehydrated on the inside at the same time. It produces oil as a defense mechanism because it lacks water. That's why products that "dry it out" make it worse: they strip what little it has and force it to produce even more oil. What you need isn't to eliminate oil — it's to balance it.`,
          fr: `Ta peau produit plus de sébum qu'elle n'en a besoin — et cet excès est ce qui bouche les pores, crée des points noirs et alimente les éruptions. Mais il y a quelque chose que beaucoup de gens ignorent : la peau grasse à tendance acnéique est souvent déshydratée en même temps. Elle produit du sébum comme mécanisme de défense parce qu'elle manque d'eau. C'est pourquoi les produits qui la "assèchent" l'aggravent. Ce dont tu as besoin, ce n'est pas d'éliminer le sébum — c'est de l'équilibrer.`,
          pt: `Sua pele produz mais sebo do que precisa — e esse excesso é o que entope os poros, cria cravos e alimenta as crises. Mas há algo que muita gente não sabe: a pele oleosa com tendência à acne costuma estar desidratada por dentro ao mesmo tempo. Ela produz sebo como mecanismo de defesa porque falta água. É por isso que os produtos que a "secam" a pioram. O que você precisa não é eliminar a oleosidade — é equilibrá-la.`,
          de: `Deine Haut produziert mehr Talg als nötig — und dieser Überschuss ist es, der die Poren verstopft, Mitesser erzeugt und Ausbrüche begünstigt. Aber es gibt etwas, das viele nicht wissen: ölige, zu Akne neigende Haut ist oft gleichzeitig von innen dehydriert. Sie produziert Talg als Schutzmechanismus, weil ihr Wasser fehlt. Deshalb machen Produkte, die sie "austrocknen", alles schlimmer. Was du brauchst, ist nicht die Fettigkeit zu eliminieren — sondern sie auszubalancieren.`,
          it: `La tua pelle produce più sebo del necessario — ed è quell'eccesso che ostruisce i pori, crea i punti neri e alimenta le riacutizzazioni. Ma c'è qualcosa che molti non sanno: la pelle grassa con tendenza all'acne è spesso disidratata all'interno allo stesso tempo. Produce sebo come meccanismo di difesa perché le manca acqua. Ecco perché i prodotti che la "asciugano" la peggiorano. Quello di cui hai bisogno non è eliminare il sebo — è bilanciarlo.`,
        },

        grasa_poros: {
          es: `Tus poros se ven abiertos porque el sebo que produce tu piel se mezcla con células muertas y tapona la abertura — con el tiempo eso estira las paredes del poro y hace que parezca más grande. Mucha gente intenta cerrarlos con agua fría o tiras adhesivas, pero eso no funciona: el poro no es un músculo que se contrae. Lo que sí funciona es mantener el interior limpio para que las paredes vuelvan a su posición natural, y proteger el colágeno alrededor para que el poro no siga agrandándose. Con la rutina correcta, la diferencia es visible.`,
          en: `Your pores look open because the oil your skin produces mixes with dead cells and plugs the opening — over time that stretches the pore walls and makes them appear larger. Many people try to close them with cold water or strips, but that doesn't work: a pore isn't a muscle that contracts. What does work is keeping the inside clean so the walls return to their natural position, and protecting the surrounding collagen so the pore doesn't keep enlarging. With the right routine, the difference is visible.`,
          fr: `Tes pores semblent ouverts parce que le sébum que produit ta peau se mélange aux cellules mortes et bouche l'ouverture — avec le temps, cela étire les parois du pore et le fait paraître plus grand. Beaucoup de gens essaient de les fermer avec de l'eau froide ou des strips, mais ça ne fonctionne pas : un pore n'est pas un muscle qui se contracte. Ce qui fonctionne, c'est garder l'intérieur propre pour que les parois retrouvent leur position naturelle.`,
          pt: `Seus poros aparecem abertos porque o sebo que sua pele produz se mistura com células mortas e tapa a abertura — com o tempo isso estica as paredes do poro e faz com que pareçam maiores. Muita gente tenta fechá-los com água fria ou tiras, mas isso não funciona: o poro não é um músculo que se contrai. O que funciona é manter o interior limpo para que as paredes voltem à posição natural, e proteger o colágeno ao redor para que o poro não continue se alargando.`,
          de: `Deine Poren sehen offen aus, weil sich der Talg deiner Haut mit abgestorbenen Zellen mischt und die Öffnung verstopft — mit der Zeit dehnt das die Porenwände und lässt sie größer erscheinen. Viele versuchen, sie mit kaltem Wasser oder Strips zu schließen, aber das funktioniert nicht: eine Pore ist kein Muskel, der sich zusammenzieht. Was funktioniert, ist das Innere sauber zu halten, damit die Wände in ihre natürliche Position zurückkehren.`,
          it: `I tuoi pori sembrano aperti perché il sebo che produce la tua pelle si mescola con le cellule morte e ostruisce l'apertura — nel tempo questo dilata le pareti del poro e lo fa sembrare più grande. Molte persone cercano di chiuderli con acqua fredda o strip, ma non funziona: il poro non è un muscolo che si contrae. Ciò che funziona è mantenere l'interno pulito affinché le pareti tornino alla posizione naturale.`,
        },

        mixta_general: {
          es: `Tu piel no es una sola piel — la zona central de la cara (frente, nariz, mentón) se comporta de una manera, y las mejillas de otra completamente distinta. La zona T produce más grasa, los poros se ven más, aparece brillo a las pocas horas. Las mejillas, en cambio, pueden estar tirantes o normales. El error más frecuente es usar el mismo producto en toda la cara: lo que calma la zona T puede secar las mejillas, y lo que hidrata las mejillas puede agravar el brillo. Tu piel necesita una rutina que entienda esa diferencia.`,
          en: `Your skin isn't one skin — the central area of the face (forehead, nose, chin) behaves one way, and the cheeks behave completely differently. The T-zone produces more oil, pores look more visible, shine appears within hours. The cheeks, on the other hand, can feel tight or normal. The most common mistake is using the same product all over the face: what calms the T-zone can dry the cheeks, and what hydrates the cheeks can worsen the shine. Your skin needs a routine that understands that difference.`,
          fr: `Ta peau n'est pas une seule peau — la zone centrale du visage (front, nez, menton) se comporte d'une façon, et les joues d'une tout autre. La zone T produit plus de sébum, les pores sont plus visibles, la brillance apparaît en quelques heures. Les joues, en revanche, peuvent être tiraillées ou normales. L'erreur la plus fréquente est d'utiliser le même produit sur tout le visage. Ta peau a besoin d'une routine qui comprend cette différence.`,
          pt: `Sua pele não é uma pele só — a zona central do rosto (testa, nariz, queixo) se comporta de um jeito, e as bochechas de outro completamente diferente. A zona T produz mais oleosidade, os poros aparecem mais, a brilhosidade surge em poucas horas. As bochechas, por outro lado, podem ficar tensas ou normais. O erro mais frequente é usar o mesmo produto em todo o rosto. Sua pele precisa de uma rotina que entenda essa diferença.`,
          de: `Deine Haut ist nicht eine Haut — der zentrale Bereich des Gesichts (Stirn, Nase, Kinn) verhält sich anders als die Wangen. Die T-Zone produziert mehr Fett, Poren sehen sichtbarer aus, Glanz erscheint nach wenigen Stunden. Die Wangen dagegen können sich straff oder normal anfühlen. Der häufigste Fehler ist, dasselbe Produkt auf dem ganzen Gesicht zu verwenden. Deine Haut braucht eine Routine, die diesen Unterschied versteht.`,
          it: `La tua pelle non è una pelle sola — la zona centrale del viso (fronte, naso, mento) si comporta in un modo, e le guance in un altro completamente diverso. La zona T produce più sebo, i pori sembrano più visibili, la lucentezza appare in poche ore. Le guance, invece, possono sentirsi tese o normali. L'errore più frequente è usare lo stesso prodotto su tutto il viso. La tua pelle ha bisogno di una routine che capisca questa differenza.`,
        },

        mixta_manchas: {
          es: `Tu piel tiene dos batallas simultáneas: la zona T produce más grasa de la que necesita, y cuando hay inflamación — aunque sea leve — la piel responde dejando una marca oscura que puede tardar meses en irse sola. Esas manchas no son cicatrices: son pigmentación que quedó atrapada después de un brote o irritación. El problema es que si no se controla la causa (el exceso de grasa y la inflamación), seguirán apareciendo. La rutina que diseñé trabaja los dos frentes a la vez: regula y aclara en paralelo.`,
          en: `Your skin has two simultaneous battles: the T-zone produces more oil than it needs, and when there's inflammation — even mild — the skin responds by leaving a dark mark that can take months to fade on its own. Those marks aren't scars: they're pigmentation trapped after a breakout or irritation. The problem is that if the cause isn't controlled (excess oil and inflammation), they'll keep appearing. The routine I designed works both fronts at once: regulates and brightens in parallel.`,
          fr: `Ta peau mène deux batailles simultanées : la zone T produit plus de sébum qu'elle n'en a besoin, et lorsqu'il y a une inflammation — même légère — la peau répond en laissant une tache sombre qui peut mettre des mois à s'estomper seule. Ces taches ne sont pas des cicatrices : c'est de la pigmentation piégée après une éruption ou une irritation. La routine que j'ai conçue travaille les deux fronts à la fois.`,
          pt: `Sua pele tem duas batalhas simultâneas: a zona T produz mais oleosidade do que precisa, e quando há inflamação — mesmo leve — a pele responde deixando uma mancha escura que pode levar meses para sumir sozinha. Essas manchas não são cicatrizes: são pigmentação presa após uma crise ou irritação. O problema é que se a causa não for controlada, continuarão aparecendo. A rotina que desenhei trabalha as duas frentes ao mesmo tempo.`,
          de: `Deine Haut kämpft an zwei Fronten gleichzeitig: Die T-Zone produziert mehr Fett als nötig, und wenn es zu Entzündungen kommt — auch leichten —, hinterlässt die Haut einen dunklen Fleck, der von alleine Monate braucht um zu verblassen. Diese Flecken sind keine Narben: Es ist Pigmentierung, die nach einem Ausbruch oder einer Reizung eingeschlossen wurde. Die Routine, die ich entworfen habe, arbeitet beide Fronten gleichzeitig.`,
          it: `La tua pelle combatte due battaglie simultanee: la zona T produce più sebo del necessario, e quando c'è infiammazione — anche lieve — la pelle risponde lasciando una macchia scura che può impiegare mesi a scomparire da sola. Quelle macchie non sono cicatrici: è pigmentazione rimasta intrappolata dopo un'eruzione o un'irritazione. La routine che ho progettato lavora entrambi i fronti contemporaneamente.`,
        },

        seca_hidratacion: {
          es: `Tu piel no retiene la hidratación bien. Absorbe agua, pero la pierde demasiado rápido — de ahí la tirantez que sientes después de limpiarla, la tendencia a descamarse, y las líneas finas que se marcan más cuando está seca. No es que le falte crema: es que la capa protectora que debería sellar esa hidratación dentro no está funcionando como debería. Cuando esa capa no está íntegra, cualquier hidratante dura poco porque el agua se escapa igual. Lo que necesitas es reconstruir esa barrera primero, y luego darle hidratación en capas para que se quede.`,
          en: `Your skin doesn't retain moisture well. It absorbs water but loses it too quickly — hence the tightness you feel after cleansing, the tendency to flake, and the fine lines that become more visible when it's dry. It's not that it lacks cream: it's that the protective layer that should seal moisture inside isn't working as it should. When that layer isn't intact, any moisturizer lasts a short time because the water escapes anyway. What you need is to rebuild that barrier first, then give it layered hydration so it stays.`,
          fr: `Ta peau ne retient pas bien l'hydratation. Elle absorbe l'eau mais la perd trop vite — d'où la sensation de tiraillement après le nettoyage, la tendance à se desquamer et les ridules qui se marquent davantage quand elle est sèche. Ce n'est pas qu'il lui manque de la crème : c'est que la couche protectrice qui devrait sceller l'hydratation à l'intérieur ne fonctionne pas comme elle devrait. Ce dont tu as besoin, c'est de reconstruire cette barrière en premier.`,
          pt: `Sua pele não retém hidratação bem. Absorve água mas a perde rápido demais — daí a sensação de tensão que você sente depois de lavar, a tendência a descamar e as linhas finas que ficam mais marcadas quando está seca. Não é que falte creme: é que a camada protetora que deveria selar essa hidratação por dentro não está funcionando como deveria. O que você precisa é reconstruir essa barreira primeiro, depois dar hidratação em camadas para que fique.`,
          de: `Deine Haut hält Feuchtigkeit nicht gut. Sie nimmt Wasser auf, verliert es aber zu schnell — daher das Spannungsgefühl nach der Reinigung, die Tendenz zur Schuppung und die feinen Linien, die sichtbarer werden wenn sie trocken ist. Es fehlt nicht an Creme: Die Schutzschicht, die die Feuchtigkeit innen versiegeln sollte, funktioniert nicht wie sie sollte. Was du brauchst, ist diese Barriere zuerst wieder aufzubauen, dann geschichtete Feuchtigkeit zu geben.`,
          it: `La tua pelle non trattiene bene l'idratazione. Assorbe acqua ma la perde troppo velocemente — da qui la sensazione di tensione dopo la detersione, la tendenza a desquamarsi e le linee sottili che si marcano di più quando è secca. Non è che manchi di crema: è che lo strato protettivo che dovrebbe sigillare l'idratazione all'interno non funziona come dovrebbe. Quello di cui hai bisogno è ricostruire quella barriera prima, poi dare idratazione a strati affinché rimanga.`,
        },

        seca_antiaging: {
          es: `Tu piel es seca y con el tiempo eso tiene un efecto directo en cómo envejece: cuando la piel no tiene suficiente hidratación y la barrera protectora está debilitada, el colágeno se degrada más rápido, las líneas de expresión se marcan antes, y la piel pierde firmeza con mayor velocidad. No es inevitable — es una consecuencia de no dar a la piel lo que necesita. La buena noticia es que la piel seca responde muy bien cuando se trabaja correctamente: con hidratación en capas, ingredientes que estimulen la renovación celular, y una protección solar constante que frene el daño que más envejece.`,
          en: `Your skin is dry and over time that has a direct effect on how it ages: when skin doesn't have enough hydration and the protective barrier is weakened, collagen degrades faster, expression lines appear earlier, and skin loses firmness more quickly. It's not inevitable — it's a consequence of not giving skin what it needs. The good news is that dry skin responds very well when worked correctly: with layered hydration, ingredients that stimulate cell renewal, and consistent sun protection that stops the damage that ages most.`,
          fr: `Ta peau est sèche et avec le temps cela a un effet direct sur son vieillissement : quand la peau n'a pas assez d'hydratation et que la barrière protectrice est affaiblie, le collagène se dégrade plus vite, les rides d'expression apparaissent plus tôt. Ce n'est pas inévitable — c'est la conséquence de ne pas donner à la peau ce dont elle a besoin. La bonne nouvelle est que la peau sèche répond très bien quand elle est bien prise en charge.`,
          pt: `Sua pele é seca e com o tempo isso tem um efeito direto em como ela envelhece: quando a pele não tem hidratação suficiente e a barreira protetora está enfraquecida, o colágeno se degrada mais rápido, as linhas de expressão aparecem antes. Não é inevitável — é consequência de não dar à pele o que ela precisa. A boa notícia é que a pele seca responde muito bem quando trabalhada corretamente.`,
          de: `Deine Haut ist trocken und das hat mit der Zeit direkte Auswirkungen darauf, wie sie altert: Wenn die Haut nicht genug Feuchtigkeit hat und die Schutzbarriere geschwächt ist, baut sich Kollagen schneller ab, Ausdruckslinien erscheinen früher. Es ist nicht unvermeidlich — es ist die Folge davon, der Haut nicht zu geben, was sie braucht. Die gute Nachricht: Trockene Haut reagiert sehr gut, wenn sie richtig gepflegt wird.`,
          it: `La tua pelle è secca e nel tempo questo ha un effetto diretto su come invecchia: quando la pelle non ha abbastanza idratazione e la barriera protettiva è indebolita, il collagene si degrada più velocemente, le linee d'espressione appaiono prima. Non è inevitabile — è la conseguenza di non dare alla pelle ciò di cui ha bisogno. La buona notizia è che la pelle secca risponde molto bene quando è lavorata correttamente.`,
        },

        sensible_rojeces: {
          es: `Tu piel reacciona fácilmente porque la capa que la protege del exterior es más fina de lo que debería. Cuando esa capa está comprometida, cualquier cosa — el agua caliente, el viento, un producto nuevo, el estrés — puede desencadenar enrojecimiento, ardor o irritación. No es que tu piel sea "difícil": es que nadie le ha dado lo que necesita para estar estable. Los enrojecimientos que describes no son solo estéticos — son la señal de que la piel está en modo de defensa constante. Con la rutina correcta, esa reactividad baja considerablemente en pocas semanas.`,
          en: `Your skin reacts easily because the layer that protects it from the outside is thinner than it should be. When that layer is compromised, anything — hot water, wind, a new product, stress — can trigger redness, burning, or irritation. It's not that your skin is "difficult": it's that nobody has given it what it needs to be stable. The redness you describe isn't just aesthetic — it's the signal that your skin is in constant defense mode. With the right routine, that reactivity drops considerably within a few weeks.`,
          fr: `Ta peau réagit facilement parce que la couche qui la protège de l'extérieur est plus fine qu'elle ne devrait l'être. Quand cette couche est compromise, n'importe quoi — l'eau chaude, le vent, un nouveau produit, le stress — peut déclencher des rougeurs, des brûlures ou des irritations. Ce n'est pas que ta peau soit "difficile" : c'est que personne ne lui a donné ce dont elle a besoin pour être stable. Avec la bonne routine, cette réactivité diminue considérablement en quelques semaines.`,
          pt: `Sua pele reage facilmente porque a camada que a protege do exterior é mais fina do que deveria. Quando essa camada está comprometida, qualquer coisa — água quente, vento, um produto novo, estresse — pode desencadear vermelhidão, ardência ou irritação. Não é que sua pele seja "difícil": é que ninguém deu a ela o que precisa para ficar estável. Com a rotina certa, essa reatividade diminui consideravelmente em poucas semanas.`,
          de: `Deine Haut reagiert leicht, weil die Schicht, die sie vor der Außenwelt schützt, dünner ist als sie sein sollte. Wenn diese Schicht beeinträchtigt ist, kann alles — heißes Wasser, Wind, ein neues Produkt, Stress — Rötungen, Brennen oder Reizungen auslösen. Es liegt nicht daran, dass deine Haut "schwierig" ist: Niemand hat ihr gegeben, was sie braucht, um stabil zu sein. Mit der richtigen Routine sinkt diese Reaktivität in wenigen Wochen erheblich.`,
          it: `La tua pelle reagisce facilmente perché lo strato che la protegge dall'esterno è più sottile di quanto dovrebbe essere. Quando quello strato è compromesso, qualsiasi cosa — acqua calda, vento, un nuovo prodotto, stress — può scatenare arrossamenti, bruciore o irritazioni. Non è che la tua pelle sia "difficile": è che nessuno le ha dato ciò di cui ha bisogno per essere stabile. Con la routine giusta, quella reattività diminuisce considerevolmente in poche settimane.`,
        },

        general_glow: {
          es: `Tu piel está bien — no hay inflamación, no hay problemas graves. Lo que describes es una piel que ha perdido luminosidad: se ve apagada, el tono no es tan uniforme como antes, y le falta ese brillo natural que debería tener. Eso pasa cuando las células de la superficie no se renuevan al ritmo que deberían — se acumulan células viejas que opacan la piel y hacen que los productos que usas no penetren bien. No es una condición difícil de mejorar: es de las que responden más rápido cuando se trabaja con los ingredientes correctos.`,
          en: `Your skin is doing well — no inflammation, no serious problems. What you describe is skin that has lost luminosity: it looks dull, the tone isn't as even as before, and it lacks that natural glow it should have. That happens when surface cells don't renew at the rate they should — old cells accumulate, making skin look dull and preventing products from penetrating well. It's not a difficult condition to improve: it's one of those that responds fastest when worked with the right ingredients.`,
          fr: `Ta peau va bien — pas d'inflammation, pas de problèmes graves. Ce que tu décris, c'est une peau qui a perdu en luminosité : elle paraît terne, le teint n'est plus aussi uniforme qu'avant, et il lui manque cet éclat naturel qu'elle devrait avoir. Cela arrive quand les cellules de surface ne se renouvellent pas au rythme qu'elles devraient. Ce n'est pas une condition difficile à améliorer : c'est l'une de celles qui répondent le plus vite avec les bons ingrédients.`,
          pt: `Sua pele está bem — sem inflamação, sem problemas graves. O que você descreve é uma pele que perdeu luminosidade: parece apagada, o tom não é tão uniforme como antes, e falta aquele brilho natural que deveria ter. Isso acontece quando as células da superfície não se renovam no ritmo que deveriam — células velhas se acumulam e impedem que os produtos penetrem bem. Não é uma condição difícil de melhorar: é uma das que responde mais rápido com os ingredientes certos.`,
          de: `Deine Haut ist in Ordnung — keine Entzündungen, keine ernsthaften Probleme. Was du beschreibst, ist eine Haut, die an Leuchtkraft verloren hat: Sie sieht matt aus, der Teint ist nicht mehr so gleichmäßig wie früher, und ihr fehlt der natürliche Glow. Das passiert, wenn sich Oberflächenzellen nicht im richtigen Rhythmus erneuern — alte Zellen häufen sich an und verhindern, dass Produkte gut eindringen. Keine schwierige Bedingung zu verbessern: eine der schnellsten Responder mit den richtigen Inhaltsstoffen.`,
          it: `La tua pelle sta bene — nessuna infiammazione, nessun problema serio. Quello che descrivi è una pelle che ha perso luminosità: sembra spenta, il tono non è uniforme come prima, e manca quel bagliore naturale che dovrebbe avere. Succede quando le cellule in superficie non si rinnovano al ritmo che dovrebbero — le cellule vecchie si accumulano e impediscono ai prodotti di penetrare bene. Non è una condizione difficile da migliorare: è una di quelle che risponde più rapidamente con gli ingredienti giusti.`,
        },

      };

      // Buscar diagnóstico por perfil, con fallback genérico
      const diagMap = dx[perfilId || ''];
      if (diagMap) return diagMap[idioma] || diagMap['en'];

      // Fallback si el perfil no coincide con ninguno conocido
      const fallbacks = {
        es: `A partir de todo lo que me contaste, tengo una imagen clara de lo que está pasando con tu piel. Diseñé esta rutina pensando exactamente en eso — no en un tipo genérico, sino en lo que tú describes.`,
        en: `From everything you've told me, I have a clear picture of what's happening with your skin. I designed this routine thinking exactly about that — not a generic type, but what you describe.`,
        fr: `D'après tout ce que tu m'as dit, j'ai une image claire de ce qui se passe avec ta peau. J'ai conçu cette routine en pensant exactement à ça.`,
        pt: `A partir de tudo que você me contou, tenho uma imagem clara do que está acontecendo com sua pele. Desenhei essa rotina pensando exatamente nisso.`,
        de: `Aus allem, was du mir erzählt hast, habe ich ein klares Bild davon, was mit deiner Haut passiert. Ich habe diese Routine genau dafür entworfen.`,
        it: `Da tutto quello che mi hai raccontato, ho un'immagine chiara di cosa sta succedendo con la tua pelle. Ho progettato questa routine pensando esattamente a quello.`,
      };
      return fallbacks[idioma] || fallbacks['en'];
    }

    // ── Cierre emocional — aplica tanto con foto como sin foto ──
    const cierreConFoto = {
      es: `**Nada en esta rutina es al azar**: cada producto fue elegido para lo que acabo de ver en tu piel hoy.`,
      en: `**Nothing in this routine is random**: every product was chosen for what I just detected in your skin today.`,
      fr: `**Rien dans cette routine n'est laissé au hasard** : chaque produit a été choisi pour ce que je viens de détecter dans ta peau aujourd'hui.`,
      pt: `**Nada nesta rotina é ao acaso**: cada produto foi escolhido para o que acabei de detectar na sua pele hoje.`,
      de: `**Nichts in dieser Routine ist zufällig**: jedes Produkt wurde für das ausgewählt, was ich heute in deiner Haut erkannt habe.`,
      it: `**Niente in questa routine è casuale**: ogni prodotto è stato scelto per quello che ho appena rilevato nella tua pelle oggi.`,
    };

    const cierreSinFoto = {
      es: `**Nada aquí es al azar**: cada paso responde a algo concreto que me contaste sobre tu piel.`,
      en: `**Nothing here is random**: every step responds to something specific you told me about your skin.`,
      fr: `**Rien n'est laissé au hasard** : chaque étape répond à quelque chose de concret que vous m'avez dit sur votre peau.`,
      pt: `**Nada aqui é ao acaso**: cada passo responde a algo concreto que você me contou sobre sua pele.`,
      de: `**Nichts hier ist zufällig**: jeder Schritt antwortet auf etwas Konkretes, das Sie mir über Ihre Haut erzählt haben.`,
      it: `**Niente qui è casuale**: ogni step risponde a qualcosa di concreto che mi hai raccontato sulla tua pelle.`,
    };

    // ── Instrucciones operativas ──
    const instrucciones = {
      es: `En cada paso de la rutina te doy **3 opciones** — todas igual de efectivas, elige la que más te resuene. Cada producto tiene un botón ❓ para saber exactamente por qué lo elegí para ti.`,
      en: `For each step of the routine you'll get **3 options** — all equally effective, pick the one that resonates most. Each product has a ❓ button to know exactly why I chose it for you.`,
      fr: `Pour chaque étape de la routine, **3 options** — toutes aussi efficaces, choisis celle qui te correspond le mieux. Chaque produit a un ❓ pour savoir exactement pourquoi je l'ai choisi.`,
      pt: `Em cada passo da rotina te dou **3 opções** — todas igualmente eficazes, escolha a que mais fizer sentido para você. Cada produto tem um ❓ para saber exatamente por que o escolhi.`,
      de: `Für jeden Schritt der Routine **3 Optionen** — alle gleich wirksam, wähle die, die am besten zu dir passt. Jedes Produkt hat einen ❓ Button, um zu erfahren, warum ich es genau für dich gewählt habe.`,
      it: `Per ogni step della routine **3 opzioni** — tutte ugualmente efficaci, scegli quella che ti convince di più. Ogni prodotto ha un ❓ per sapere esattamente perché l'ho scelto per te.`,
    };

    // ── Ocultar barra de input desde el primer instante del reveal ──
    // El usuario no debe poder escribir mientras KOI está generando
    // el diagnóstico y los mensajes de la fase insight.
    setInputAreaVisible(false);
    setInputHabilitado(false);

    mostrarTyping();
    await new Promise(r => setTimeout(r, visionReveal ? 1600 : 1200));
    ocultarTyping();

    // ── Construir mensaje final ──
    const diagnosticoQuiz = _construirDiagnosticoQuiz(perfilId, tipoPiel, sensib, preocupAll, idioma);
    const cierre  = visionReveal
      ? (cierreConFoto[idioma]  || cierreConFoto['en'])
      : (cierreSinFoto[idioma]  || cierreSinFoto['en']);
    const instrs  = instrucciones[idioma] || instrucciones['en'];

    // CON FOTO: diagnóstico del quiz → diagnóstico visual del Worker → cierre → instrucciones
    // SIN FOTO: diagnóstico del quiz →  cierre → instrucciones
    const msg = visionReveal
      ? `${diagnosticoQuiz}\n\n${visionReveal}\n\n${cierre}\n\n${instrs}`
      : `${diagnosticoQuiz}\n\n${cierre}\n\n${instrs}`;
    const textEl = agregarMensaje('koi', '', true);
    if (textEl) await escribirConEfecto(textEl, msg);
    KOI_STATE.historial.push({ role: 'assistant', content: msg });
    guardarHistorialLocal();

    // ── Botón "Ver mi rutina" — en #koi-messages, debajo del mensaje de KOI.
    // El input queda BLOQUEADO hasta que el usuario haga clic y vea la rutina.
    // Así el usuario lee el mensaje completo de KOI antes de poder escribir.
    const scrollBtns = {
      es: '✨ Ver mi rutina ahora',
      en: '✨ Show me my routine',
      fr: '✨ Voir ma routine',
      pt: '✨ Ver minha rotina agora',
      de: '✨ Routine jetzt ansehen',
      it: '✨ Vedi la mia routine',
    };
    const btnLabel = scrollBtns[idioma] || scrollBtns['en'];

    // Ocultar barra de input — ya se hizo desde mostrarChips('reveal')
    // o desde pedirEmailEnChat(). Nos aseguramos aquí también por si
    // revelarRutinaConKOI se llama sin pasar por esos flujos.
    setInputAreaVisible(false);

    setTimeout(() => {
      const container = document.getElementById('koi-messages');
      if (!container || document.getElementById('koi-reveal-btn-persistent')) return;

      const wrapper = document.createElement('div');
      wrapper.id        = 'koi-reveal-btn-persistent';
      wrapper.className = 'koi-reveal-btn-persistent';

      const btn = document.createElement('button');
      btn.className   = 'koi-reveal-btn-persistent__btn';
      btn.textContent = btnLabel;
      btn.addEventListener('click', () => {
        // Ocultar el botón una vez usado
        wrapper.classList.add('koi-reveal-btn-persistent--used');
        setTimeout(() => { if (wrapper.parentNode) wrapper.remove(); }, 300);

        // 1. Scroll hacia los productos
        const rutinaEl = document.getElementById('shatokb-resultado') ||
                         document.getElementById('stk-reveal-section');
        if (rutinaEl) rutinaEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // 2. Revelar productos
        setTimeout(() => {
          if (typeof window.shatokbRevelarProductos === 'function') {
            window.shatokbRevelarProductos();
          }
        }, 300);

        // 3. Mostrar barra de input + habilitar + chips de bienvenida
        //    + activar mini barra de carrito (ahora el usuario ya vio la rutina)
        setTimeout(() => {
          setInputAreaVisible(true);
          setInputHabilitado(true);
          mostrarChips('bienvenida');
          // Activar mini barra — el usuario acaba de ver los productos,
          // ahora tiene sentido mostrar el precio y el botón de compra
          window.shatokbMostrarMiniCartBar();
        }, 1800);
      });

      wrapper.appendChild(btn);
      container.appendChild(wrapper);
      scrollAlFinal();
    }, 700);
  }

  // Momento 5 eliminado — el cuadro sticky "Add my full routine to cart"
  // que aparece encima del chat ya cumple esta función correctamente y
  // siempre está visible. Un segundo botón dentro del chat es redundante
  // y además llevaría a /cart vacío (los productos se añaden desde los
  // cards de la rutina, no desde el chat).

  /* ══════════════════════════════════════════════════════════
     ENVÍO DE MENSAJES
     ══════════════════════════════════════════════════════════ */

  async function enviarMensajeUsuario () {
    const input = document.getElementById('koi-input');
    if (!input) return;

    const texto = input.value.trim();
    if (!texto || KOI_STATE.isTyping) return;

    // Limpiar input
    input.value = '';
    input.style.height = 'auto';
    ocultarChips();

    // Mostrar mensaje del usuario
    agregarMensaje('user', texto);
    KOI_STATE.msgCount++;

    // Añadir al historial
    KOI_STATE.historial.push({ role: 'user', content: texto });
    guardarHistorialLocal();

    // Deshabilitar input mientras KOI responde
    setInputHabilitado(false);

    // Obtener respuesta de KOI
    await obtenerRespuestaKOI(texto);

    // Re-habilitar input
    setInputHabilitado(true);
    input.focus();

    // Mostrar chips contextuales después de algunos mensajes
    if (KOI_STATE.msgCount >= 2) {
      setTimeout(() => mostrarChips('post_rutina'), 500);
    }
  }

  /* ══════════════════════════════════════════════════════════
   SPRINT 2 — KOI VISION: manejar resultado del análisis facial
   Llamado desde window.koiVision.onResultado() cuando el
   usuario confirma "Ver análisis completo en el chat"
   ══════════════════════════════════════════════════════════ */
async function manejarResultadoVision (data) {
  const { result, image, ctx } = data || {};
  const idioma = detectarIdioma();

  // El mensaje del usuario YA fue añadido por enviarDesdeChip / manejarChipPostCamara
  // cuando el usuario pulsó el chip. NO añadir de nuevo — evita duplicados.
  // Solo añadir al historial si llegamos aquí via evento global (sin chip previo).
  const ultimoMsgUser = KOI_STATE.historial.filter(m => m.role === 'user').slice(-1)[0];
  const yaEstaEnHistorial = ultimoMsgUser && ultimoMsgUser.content.includes('📸');
  const yaEstaEnDOM = !!document.querySelector('#koi-messages .koi-msg--user:last-child');
  if (!yaEstaEnHistorial) {
    const chipTexto = idioma === 'es' ? '📸 Analicé mi piel con la cámara' : '📸 I analyzed my skin with the camera';
    KOI_STATE.historial.push({ role: 'user', content: chipTexto });
    if (!yaEstaEnDOM) agregarMensaje('user', chipTexto);
  }

  mostrarTyping();

  // ══════════════════════════════════════════════════════════════════
  // ★★★ ENRIQUECIMIENTO FOTO + QUIZ — v8.0 "COMBINADO REAL" ★★★
  //
  // IMPORTANTE: este bloque se ejecuta SIEMPRE que el Worker devuelva
  // dimensiones — independientemente de si hay mensaje_koi o no.
  // Es el núcleo de la promesa "quiz + foto combinados".
  //
  // Flujo:
  //   1. Worker /vision devuelve dimensiones (8 scores clínicos reales)
  //   2. shatokbEnriquecerRespuestasConVision() traduce esos scores
  //      a señales que el scorer ya conoce (tipo_piel, concerns, sensibilidad)
  //   3. shatokbCalcularPerfil() recalcula el perfil óptimo con la señal combinada
  //   4. shatokbCambiarPerfil() re-rankea los 250 productos con ese perfil
  //      y las respuestas enriquecidas → rutina personalizada por piel REAL
  //
  // Solo se salta si el Worker no devolvió ninguna dimensión (error real).
  // ══════════════════════════════════════════════════════════════════
  const tieneDimensiones = result && result.dimensiones
    && typeof result.dimensiones === 'object'
    && Object.keys(result.dimensiones).length > 0;

  let respuestasEnriquecidas = null;
  let perfilRecalculado      = null;
  let huboCambioPerfilReal   = false;

  if (tieneDimensiones) {
    // Guardar resultado completo en estado global
    KOI_STATE.visionResult = result;

    // ── A) Enriquecer respuestas del quiz con los 8 scores fotográficos ──
    if (typeof window.shatokbEnriquecerRespuestasConVision === 'function') {
      const respuestasBase = KOI_STATE.contexto?.respuestas || {};
      respuestasEnriquecidas = window.shatokbEnriquecerRespuestasConVision(respuestasBase, result);

      console.log('[KOI Vision v8] ✅ Respuestas enriquecidas foto+quiz:', {
        tipo_piel:    respuestasBase.tipo_piel + ' → ' + respuestasEnriquecidas.tipo_piel,
        sensibilidad: respuestasBase.sensibilidad + ' → ' + respuestasEnriquecidas.sensibilidad,
        concerns:     [respuestasEnriquecidas.preocupacion, ...(respuestasEnriquecidas.preocupacion_secundaria || [])],
        score_global: result.score_global,
      });
    }

    // ── B) Re-aplicar al scorer — siempre, aunque el perfil no cambie ──
    // La foto puede no cambiar el perfil pero sí los concerns secundarios
    // y la sensibilidad → los productos rankeados serán diferentes y más precisos.
    if (typeof window.shatokbCambiarPerfil === 'function') {
      // Determinar perfil de destino:
      //   1. Si el Worker propuso ajuste_perfil → usarlo como hint
      //   2. shatokbCambiarPerfil recalcula internamente con las respuestas enriquecidas
      //      usando shatokbCalcularPerfil() → señal foto+quiz combinada
      const ajuste = result.ajuste_perfil;
      const perfilHint = (ajuste && ajuste.nuevo_perfil_id)
        ? ajuste.nuevo_perfil_id
        : (KOI_STATE.contexto?.perfil?.id || '');

      const perfilAntes = KOI_STATE.contexto?.perfil?.id || '';

      try {
        const exito = await Promise.race([
          window.shatokbCambiarPerfil(perfilHint, respuestasEnriquecidas),
          new Promise(r => setTimeout(r, 4000)) // timeout 4s — nunca bloquear UI
        ]);

        // Detectar si el perfil real cambió (para mensaje WOW)
        const perfilDespues = window.shatokbState?.perfilOverride || perfilHint;
        huboCambioPerfilReal = exito === true && perfilDespues !== perfilAntes;
        perfilRecalculado    = perfilDespues;

        if (huboCambioPerfilReal) {
          console.log('[KOI Vision v8] 🔄 Perfil reasignado por foto:', perfilAntes, '→', perfilDespues);
        } else {
          console.log('[KOI Vision v8] ✅ Mismo perfil, productos re-rankeados con foto:', perfilDespues);
        }

        // Actualizar contexto de KOI con el perfil final
        if (KOI_STATE.contexto && perfilDespues) {
          KOI_STATE.contexto.perfil = {
            id:     perfilDespues,
            nombre: perfilDespues,
          };
        }
      } catch(e) {
        console.warn('[KOI Vision v8] shatokbCambiarPerfil error:', e);
      }
    }
  }

  // ══════════════════════════════════════════════════════════════════
  // ★ MENSAJE DE KOI — después del enriquecimiento
  // ══════════════════════════════════════════════════════════════════

  // ── Si tenemos resultado real del Worker con mensaje ──────────────
  if (result && result.mensaje_koi) {
    await new Promise(r => setTimeout(r, 900));
    ocultarTyping();

    // 1. Mensaje corto de apertura — la tarjeta habla sola, no repetir el diagnóstico
    const mensajesApertura = {
      es: 'Listo. Esto es lo que encontré en tu piel hoy.',
      en: 'Done. This is what I found in your skin today.',
      fr: 'Voilà. C\'est ce que j\'ai trouvé dans ta peau aujourd\'hui.',
      pt: 'Pronto. Isso é o que encontrei na sua pele hoje.',
      de: 'Fertig. Das habe ich heute in deiner Haut gefunden.',
      it: 'Fatto. Questo è quello che ho trovato nella tua pelle oggi.',
    };
    let mensajeFinal = mensajesApertura[idioma] || mensajesApertura['en'];

    // Si la foto recalculó un perfil diferente al del quiz → añadir nota WOW
    if (huboCambioPerfilReal && perfilRecalculado) {
      const notaReasignacion = {
        es: `\n\n✦ *Tu foto reveló que tu piel es diferente a lo que indicaste en el quiz. He ajustado tu rutina en tiempo real.*`,
        en: `\n\n✦ *Your photo revealed your skin differs from your quiz answers. I've updated your routine in real time.*`,
        fr: `\n\n✦ *Votre photo révèle que votre peau est différente de vos réponses au quiz. J'ai mis à jour votre routine en temps réel.*`,
        pt: `\n\n✦ *Sua foto revelou que sua pele é diferente do que você indicou no quiz. Atualizei sua rotina em tempo real.*`,
        de: `\n\n✦ *Dein Foto hat gezeigt, dass deine Haut anders ist als deine Quiz-Antworten. Ich habe deine Routine in Echtzeit angepasst.*`,
        it: `\n\n✦ *La tua foto ha rivelato che la tua pelle è diversa da quanto indicato nel quiz. Ho aggiornato la tua routine in tempo reale.*`,
      };
      mensajeFinal += (notaReasignacion[idioma] || notaReasignacion.en);
    }

    const textEl = agregarMensaje('koi', '', false);
    await escribirConEfecto(textEl, mensajeFinal);
    KOI_STATE.historial.push({ role: 'koi', content: mensajeFinal });

    await new Promise(r => setTimeout(r, 400));

    // 2. Card de análisis avanzado — 8 dimensiones clínicas
    const bubble = textEl.closest
      ? textEl.closest('.koi-msg') || textEl.parentElement
      : textEl.parentElement;

    const cardEl = _construirCardAnalisis(result, idioma);
    if (cardEl) {
      const chatContainer = document.getElementById('koi-messages') ||
                            document.getElementById('shatokb-koi-messages') ||
                            bubble?.parentElement;
      if (chatContainer) {
        // Envolver la card en un mensaje KOI para que herede el layout del chat
        const cardWrapper = document.createElement('div');
        cardWrapper.className = 'koi-msg koi-msg--koi koi-msg--card';
        cardWrapper.appendChild(cardEl);
        chatContainer.appendChild(cardWrapper);
        _animarCardEntrada(cardEl);
      }
    }

    guardarHistorialLocal();
    KOI_STATE.revealPhase = 'post_vision';
    setTimeout(() => mostrarChips('post_vision'), 700);

  } else {
    // ── Fallback: Worker no devolvió mensaje_koi ──────────────────────
    // El enriquecimiento foto+quiz YA se ejecutó arriba si había dimensiones.
    // Aquí solo mostramos el mensaje al usuario.
    // Si tieneDimensiones=true, el scorer ya re-rankeó los productos.
    // Si tieneDimensiones=false, trabajamos solo con el quiz.

    await new Promise(r => setTimeout(r, 1400));
    ocultarTyping();

    // ── Extraer datos: preferir respuestas enriquecidas si existen ───
    const ctx       = KOI_STATE.contexto || {};
    // Usar respuestas enriquecidas con foto si ya se calcularon arriba,
    // si no, usar las respuestas base del quiz
    const resp      = respuestasEnriquecidas || ctx.respuestas || ctx.answers || {};
    const perfil    = ctx.perfil       || ctx.profile    || {};
    const perfilId  = (perfilRecalculado || perfil.id || perfil.perfil_id || ctx.perfil_id || '').toLowerCase();
    const preoc     = (resp.preocupacion || resp.concern || resp.skin_concern || resp.preocupacion_principal || '').toLowerCase();
    const tipoP     = (resp.tipo_piel  || resp.skin_type || resp.tipo        || '').toLowerCase();
    const zona      = (resp.zona_problematica || resp.problem_zone || '').toLowerCase();
    const edad      = resp.edad || resp.age || '';

    // ── Resolver concern dominante: primero preocupacion, luego perfil_id ──
    const hayAcne   = preoc.includes('acn') || preoc.includes('grano') || preoc.includes('blemish') || perfilId.includes('acne');
    const hayManchas= preoc.includes('mancha') || preoc.includes('pigment') || preoc.includes('spot') || preoc.includes('dark') || perfilId.includes('pigment') || perfilId.includes('mancha');
    const hayPoros  = preoc.includes('por') || preoc.includes('pore') || preoc.includes('textur') || perfilId.includes('pore');
    const hayArrugas= preoc.includes('arruga') || preoc.includes('aging') || preoc.includes('firmez') || preoc.includes('wrink') || perfilId.includes('aging') || perfilId.includes('anti_age');
    const hayRojez  = preoc.includes('rojez') || preoc.includes('sensibl') || preoc.includes('redness') || preoc.includes('rosacea') || perfilId.includes('sensit') || perfilId.includes('calming');
    const hayDeshi  = preoc.includes('deshidrat') || preoc.includes('sequ') || preoc.includes('dry') || preoc.includes('tight') || tipoP.includes('seca') || tipoP.includes('dry') || perfilId.includes('dry') || perfilId.includes('barrier');
    const hayBrillo = preoc.includes('brillo') || preoc.includes('oil') || preoc.includes('seborr') || tipoP.includes('grasa') || tipoP.includes('oily') || perfilId.includes('oily');

    // ── Seleccionar mensaje según concern dominante ──
    let mensajeFallback = '';

    if (idioma === 'es') {
      if (hayAcne) {
        mensajeFallback = `Revisé tu perfil y hay algo que me llama mucho la atención. 🔬\n\nCuando el acné aparece una y otra vez, casi siempre es porque hay inflamación dentro del poro — no en la superficie. Lo que ves afuera es solo la señal de algo que está pasando más adentro. Y cuando intentas secarlo con productos fuertes, tu piel se defiende produciendo más grasa, y eso... alimenta el problema.\n\nEl truco no es atacar el grano. Es calmar la inflamación, equilibrar la piel y cerrar el ciclo desde dentro. Ya sé qué necesita tu piel para hacer exactamente eso. ¿Vemos tu rutina?`;
      } else if (hayManchas) {
        mensajeFallback = `Lo que me dice tu análisis explica mucho de lo que probablemente ya viviste. 🔬\n\nLas manchas que no se van con crema tienen una razón: están en una capa más profunda de la piel, donde la mayoría de los productos no llegan. Por eso se aclaran un poco y vuelven — porque nunca se trabajó donde realmente están.\n\nPara borrarlas de verdad, hay que frenar la producción de pigmento desde dentro, no solo aclarar por fuera. Eso requiere los ingredientes correctos, en el orden correcto. Ya los tengo identificados para tu caso. ¿Los vemos?`;
      } else if (hayPoros) {
        mensajeFallback = `Tu análisis me cuenta algo que tiene mucho sentido. 🔬\n\nLos poros no se "cierran" — eso es un mito. Lo que sí pasa es que cuando se acumula grasa y células muertas dentro, se estiran y se ven más grandes. Tu piel está produciendo más grasa de la que necesita, y eso agranda el poro y da esa textura que tanto molesta.\n\nLa solución no es limpiar más fuerte — es regular la producción de grasa y renovar la piel suavemente. Con los ingredientes correctos, los poros se ven notablemente más pequeños en semanas. Tu rutina ya tiene eso. ¿La vemos?`;
      } else if (hayArrugas) {
        mensajeFallback = `Tu análisis me habla de algo que es completamente normal, pero que tiene solución. 🔬\n\nA partir de cierta edad, la piel produce menos colágeno — esa proteína que la mantiene firme y tersa. Las líneas que ves son la señal de que ese proceso empezó. No es un defecto, es biología. Pero sí se puede frenar y revertir en parte.\n\nHay ingredientes que le dan a la piel la señal de volver a producir colágeno — y funcionan de verdad cuando se usan correctamente. Ya tengo el protocolo exacto para tu perfil. ¿Lo vemos?`;
      } else if (hayRojez) {
        mensajeFallback = `Lo que veo en tu perfil me dice que tu piel está a la defensiva. 🔬\n\nCuando la piel se pone roja fácilmente, se irrita con productos que a otros no les hacen nada, o sientes calor o escozor sin razón aparente — eso no es que seas "exagerada". Es que tu barrera protectora está debilitada y deja entrar cosas que no debería.\n\nLa clave no es evitar todo — es reconstruir esa barrera para que la piel vuelva a protegerse sola. Con los ingredientes correctos, la piel deja de reaccionar tanto en pocas semanas. Tu rutina está pensada exactamente para eso. ¿La vemos?`;
      } else if (hayDeshi) {
        mensajeFallback = `Tu perfil me cuenta algo que mucha gente confunde. 🔬\n\nEsa sensación de tirantez, esa opacidad, esas líneas finas que aparecen aunque uses crema — no siempre es que la piel sea "seca". Muchas veces es que la piel está perdiendo agua más rápido de lo que puede retenerla. Es diferente, y se trata diferente.\n\nLo que necesitas no es solo más hidratante — es sellar la piel para que el agua que ya tiene no se evapore. Hay ingredientes que hacen exactamente eso. Ya los tengo listos en tu rutina. ¿La vemos?`;
      } else if (hayBrillo) {
        mensajeFallback = `Tu análisis me confirma algo que muchas personas con piel grasa no saben. 🔬\n\nCuando la piel brilla demasiado, el instinto es secarla. Pero eso empeora el problema — porque la piel interpreta esa sequedad como una amenaza y produce aún más grasa para compensar. Es un círculo vicioso.\n\nLo que realmente funciona es enseñarle a la piel que no necesita producir tanta grasa. Con los ingredientes correctos, en pocas semanas el brillo se regula solo y la piel queda mate sin sentirse apretada. Tu rutina ya tiene eso. ¿La vemos?`;
      } else {
        mensajeFallback = `Tu perfil me da información muy clara sobre lo que está pasando en tu piel. 🔬\n\nLo que yo hago es leer esas señales y traducirlas en un plan concreto. Porque la piel siempre tiene una razón detrás de cada problema — y cuando encuentras esa razón, la solución deja de ser ensayo y error.\n\nYa tengo tu rutina armada con los ingredientes exactos para lo que tu piel necesita ahora. Cada paso tiene un porqué específico para ti. ¿La vemos juntas?`;
      }
    } else if (idioma === 'fr') {
      if (hayAcne) {
        mensajeFallback = `Ce que je vois dans ton profil est très clair. 🔬\n\nQuand l'acné revient sans cesse, c'est presque toujours parce qu'il y a une inflammation à l'intérieur du pore — pas en surface. Ce que tu vois dehors n'est que le signal de quelque chose qui se passe plus en profondeur. Et quand tu essaies de l'assécher avec des produits forts, ta peau se défend en produisant plus de sébum, ce qui aggrave le problème.\n\nJ'ai identifié exactement ce dont ta peau a besoin pour briser ce cycle. Ta routine est prête. On la voit ?`;
      } else if (hayManchas) {
        mensajeFallback = `Ton analyse m'explique beaucoup de choses. 🔬\n\nLes taches qui ne partent pas avec les crèmes habituelles sont souvent dans une couche plus profonde de la peau — là où la plupart des produits n'arrivent pas. C'est pourquoi elles s'éclaircissent un peu puis reviennent.\n\nPour les effacer vraiment, il faut freiner la production de pigment de l'intérieur. J'ai les ingrédients exacts pour ton cas. Ta routine est personnalisée. On la découvre ?`;
      } else if (hayArrugas) {
        mensajeFallback = `Ton analyse me parle de quelque chose de très normal, mais qui a une solution. 🔬\n\nÀ partir d'un certain âge, la peau produit moins de collagène — cette protéine qui la maintient ferme et lisse. Les rides que tu vois sont le signe que ce processus a commencé. Ce n'est pas un défaut, c'est de la biologie. Mais on peut le ralentir et en partie l'inverser.\n\nJ'ai les ingrédients qui donnent à la peau le signal de relancer sa production de collagène. Ta routine est prête avec le protocole exact pour ton profil. On la voit ?`;
      } else if (hayRojez) {
        mensajeFallback = `Ce que je lis dans ton profil me dit que ta peau est sur la défensive. 🔬\n\nQuand la peau rougit facilement ou réagit à des produits qui ne posent aucun problème aux autres, ce n'est pas une coïncidence — c'est que ta barrière protectrice est fragilisée. La clé, c'est de la reconstruire pour que la peau recommence à se protéger toute seule.\n\nTa routine est pensée exactement pour ça. On la voit ensemble ?`;
      } else {
        mensajeFallback = `Ton profil me donne des informations très claires sur ce qui se passe dans ta peau. 🔬\n\nCe que je fais, c'est lire ces signaux et les traduire en un plan concret. Parce que la peau a toujours une raison derrière chaque problème — et quand on trouve cette raison, la solution n'est plus un essai-erreur.\n\nTa routine est déjà construite avec les ingrédients exacts pour ce dont ta peau a besoin. Chaque étape a un pourquoi précis pour toi. On la découvre ?`;
      }
    } else if (idioma === 'pt') {
      if (hayAcne) {
        mensajeFallback = `O que vejo no seu perfil é muito claro. 🔬\n\nQuando a acne volta repetidamente, quase sempre é porque há inflamação dentro do poro — não na superfície. Quanto mais você tenta secar com produtos fortes, mais a pele se defende produzindo mais gordura, e isso... alimenta o problema.\n\nJá identifiquei o que sua pele precisa para romper esse ciclo de verdade. Sua rotina está pronta. Vamos vê-la?`;
      } else if (hayManchas) {
        mensajeFallback = `Sua análise me explica muito. 🔬\n\nAs manchas que não somem com crème estão numa camada mais profunda da pele — onde a maioria dos produtos não chega. Por isso clareiam um pouco e voltam.\n\nPara apagá-las de verdade, é preciso frear a produção de pigmento por dentro. Já tenho os ingredientes certos para o seu caso. Sua rotina está personalizada. Vamos vê-la?`;
      } else if (hayArrugas) {
        mensajeFallback = `Sua análise fala de algo completamente normal, mas que tem solução. 🔬\n\nCom o tempo, a pele produz menos colágeno — a proteína que a mantém firme. As linhas que você vê são o sinal disso. Mas é possível desacelerar e parcialmente reverter esse processo com os ingredientes certos.\n\nJá tenho o protocolo exato para o seu perfil. Sua rotina está pronta. Vamos vê-la?`;
      } else {
        mensajeFallback = `Seu perfil me dá informações muito claras sobre o que está acontecendo na sua pele. 🔬\n\nO que eu faço é ler esses sinais e transformá-los num plano concreto. Porque a pele sempre tem um motivo por trás de cada problema — e quando você encontra esse motivo, a solução deixa de ser tentativa e erro.\n\nSua rotina já está montada com os ingredientes exatos para o que sua pele precisa agora. Vamos vê-la?`;
      }
    } else {
      // English (default + de, it, etc.)
      if (hayAcne) {
        mensajeFallback = `Your profile tells me something that explains a lot. 🔬\n\nWhen breakouts keep coming back, it's almost always because there's inflammation building up inside the pore — not just on the surface. What you see on the outside is just the signal of something happening deeper in. And when you try to dry it out with strong products, your skin fights back by producing more oil, which makes it worse.\n\nThe trick isn't attacking the pimple — it's calming the inflammation and breaking the cycle from within. I already know what your skin needs to do exactly that. Ready to see your routine?`;
      } else if (hayManchas) {
        mensajeFallback = `What your analysis tells me explains a lot of what you've probably already experienced. 🔬\n\nSpots that won't go away with regular creams are usually sitting in a deeper layer of the skin — where most products can't reach. That's why they fade a little and come back — because nothing ever worked where they actually are.\n\nTo truly erase them, you need to slow down pigment production from the inside, not just lighten from the outside. That takes the right ingredients, in the right order. I've already got them identified for your case. Shall we look?`;
      } else if (hayPoros) {
        mensajeFallback = `Your analysis tells me something that makes a lot of sense. 🔬\n\nPores don't actually "open" or "close" — that's a myth. What really happens is that when oil and dead skin cells build up inside, they stretch and look bigger. Your skin is producing more oil than it needs right now, and that's what's causing that texture you don't like.\n\nThe solution isn't scrubbing harder — it's gently regulating your skin's oil and speeding up cell renewal. With the right ingredients, pores look noticeably smaller within weeks. Your routine already has that covered. Want to see it?`;
      } else if (hayArrugas) {
        mensajeFallback = `Your analysis speaks to something completely normal — but with a real solution. 🔬\n\nFrom a certain age, the skin produces less collagen — the protein that keeps it firm and smooth. The lines you're seeing are the signal that this process has started. It's not a flaw, it's just biology. But it can be slowed down, and partly reversed.\n\nThere are ingredients that give the skin the signal to start producing collagen again — and they genuinely work when used correctly. I already have the exact protocol for your profile. Shall we look at it?`;
      } else if (hayRojez) {
        mensajeFallback = `What I see in your profile tells me your skin is on the defensive. 🔬\n\nWhen skin flushes easily, reacts to products that don't bother anyone else, or feels hot or tingly for no clear reason — that's not just sensitivity. It means your skin's protective barrier has weakened and is letting things in that it shouldn't.\n\nThe key isn't avoiding everything — it's rebuilding that barrier so your skin can protect itself again. With the right ingredients, the reactivity calms down in just a few weeks. Your routine is designed specifically for this. Shall we see it?`;
      } else if (hayDeshi) {
        mensajeFallback = `Your profile tells me something a lot of people get confused about. 🔬\n\nThat tight feeling, that dull look, those fine lines that show up even when you moisturize — that's not always "dry skin." A lot of the time it means your skin is losing water faster than it can hold onto it. That's a different problem, and it needs a different fix.\n\nWhat you need isn't just more moisturizer — it's sealing the skin so the water already in it stops evaporating. There are ingredients that do exactly that. They're already in your routine. Shall we look?`;
      } else if (hayBrillo) {
        mensajeFallback = `Your analysis confirms something a lot of people with oily skin don't know. 🔬\n\nWhen skin gets too shiny, the instinct is to dry it out. But that actually makes it worse — because skin reads that dryness as a threat and produces even more oil to compensate. It's a cycle that keeps feeding itself.\n\nWhat actually works is teaching your skin it doesn't need to produce so much oil in the first place. With the right ingredients, shine regulates on its own within weeks — and your skin stays matte without feeling tight. Your routine already has this. Want to see it?`;
      } else {
        mensajeFallback = `Your profile gives me a very clear picture of what's going on with your skin. 🔬\n\nWhat I do is read those signals and turn them into a concrete plan. Because skin always has a reason behind every problem — and when you find that reason, the solution stops being trial and error.\n\nYour routine is already built with the exact ingredients your skin needs right now. Every step has a specific reason for being there — for you. Ready to take a look?`;
      }
    }

    // ── Si la foto recalculó un perfil diferente, añadir nota WOW ──
    if (huboCambioPerfilReal && perfilRecalculado) {
      const notaReasignacionFallback = {
        es: `\n\n✦ *Algo importante: tu foto reveló que tu piel es diferente a lo que indicaste en el quiz. He ajustado tu rutina en tiempo real para reflejar lo que realmente veo en tu piel.*`,
        en: `\n\n✦ *Something important: your photo revealed your skin is different from what you indicated in the quiz. I've updated your routine in real time to reflect what I actually see.*`,
        fr: `\n\n✦ *Important: votre photo révèle que votre peau est différente de vos réponses. J'ai mis à jour votre routine en temps réel.*`,
        pt: `\n\n✦ *Importante: sua foto revelou que sua pele é diferente do que você indicou. Atualizei sua rotina em tempo real.*`,
      };
      mensajeFallback += (notaReasignacionFallback[idioma] || notaReasignacionFallback.en);
    }

    // ── Mostrar mensaje con efecto typewriter ────────────────
    const textEl = agregarMensaje('koi', '', false);
    await escribirConEfecto(textEl, mensajeFallback);
    KOI_STATE.historial.push({ role: 'koi', content: mensajeFallback });

    guardarHistorialLocal();

    await new Promise(r => setTimeout(r, 600));

    // ── Chips post_vision: solo "muéstrame mi rutina" ────────
    KOI_STATE.revealPhase = 'post_vision';
    mostrarChips('post_vision');
  }

  scrollAlFinal();
}

/* ══════════════════════════════════════════════════════════
   CONSTRUIR CARD DE ANÁLISIS AVANZADO
   Renderiza el resultado de las 8 dimensiones + ingredientes
   + protocolo urgente como card visual dentro del chat.
   ══════════════════════════════════════════════════════════ */
function _construirCardAnalisis(result, idioma) {
  if (!result) return null;

  const dim  = result.dimensiones || {};
  const zona = result.zonas || {};
  const ings = result.ingredientes_prioritarios || [];
  const pts  = result.puntos_criticos || [];

  // Labels por idioma
  const L = {
    es: {
      titulo:        'Análisis Cutáneo KOI',
      scoreGlobal:   'Salud global de piel',
      scoreLabel:    (s) => s >= 8 ? 'Piel en excelente estado' : s >= 6 ? 'Piel en buen estado' : s >= 4 ? 'Piel con áreas de mejora' : 'Piel requiere atención urgente',
      zonas:         'Diagnóstico por zonas',
      dimensiones:   'Análisis dimensional',
      criticos:      'Hallazgos críticos',
      ingredientes:  'Activos prioritarios',
      protocolo:     'Protocolo urgente',
      edadBio:       'Edad biológica estimada',
      perfil:        'Perfil confirmado',
      perfilNo:      'Ajuste de perfil detectado',
      dim_labels: {
        hidratacion:  'Hidratación',
        barrera:      'Barrera cutánea',
        sebum:        'Control sebáceo',
        pigmentacion: 'Pigmentación',
        textura:      'Textura',
        circulacion:  'Circulación',
        firmeza:      'Firmeza',
        microbioma:   'Microbioma',
      },
      dim_icons: {
        hidratacion:  '💧',
        barrera:      '🛡️',
        sebum:        '✨',
        pigmentacion: '🌗',
        textura:      '🔎',
        circulacion:  '❤️',
        firmeza:      '💪',
        microbioma:   '🦠',
      },
    },
    en: {
      titulo:        'KOI Skin Analysis',
      scoreGlobal:   'Overall skin health',
      scoreLabel:    (s) => s >= 8 ? 'Excellent skin condition' : s >= 6 ? 'Good skin condition' : s >= 4 ? 'Skin has areas to improve' : 'Skin needs urgent attention',
      zonas:         'Zone-by-zone diagnosis',
      dimensiones:   'Dimensional analysis',
      criticos:      'Critical findings',
      ingredientes:  'Priority actives',
      protocolo:     'Urgent protocol',
      edadBio:       'Estimated biological age',
      perfil:        'Profile confirmed',
      perfilNo:      'Profile adjustment detected',
      dim_labels: {
        hidratacion:  'Hydration',
        barrera:      'Skin barrier',
        sebum:        'Sebum control',
        pigmentacion: 'Pigmentation',
        textura:      'Texture',
        circulacion:  'Circulation',
        firmeza:      'Firmness',
        microbioma:   'Microbiome',
      },
      dim_icons: {
        hidratacion:  '💧',
        barrera:      '🛡️',
        sebum:        '✨',
        pigmentacion: '🌗',
        textura:      '🔎',
        circulacion:  '❤️',
        firmeza:      '💪',
        microbioma:   '🦠',
      },
    },
  };
  const lbl = L[idioma] || L.en;

  // ── Helpers de color y barra ──────────────────────────────
  function scoreColor(s) {
    if (s === null || s === undefined) return '#6b7280';
    if (s >= 8) return '#22c55e';
    if (s >= 6) return '#84cc16';
    if (s >= 4) return '#f59e0b';
    return '#ef4444';
  }
  function scoreGrade(s) {
    if (s === null || s === undefined) return '—';
    if (s >= 9) return 'A+';
    if (s >= 8) return 'A';
    if (s >= 7) return 'B+';
    if (s >= 6) return 'B';
    if (s >= 5) return 'C+';
    if (s >= 4) return 'C';
    return 'D';
  }
  function scoreBar(s) {
    if (s === null || s === undefined) return '';
    const pct   = Math.round((s / 10) * 100);
    const color = scoreColor(s);
    return `<div class="kva-score-bar"><div class="kva-score-fill" style="width:${pct}%;background:${color};box-shadow:0 0 8px ${color}55;"></div></div>`;
  }

  // ── Score global (hero section) ───────────────────────────
  const sg = (typeof result.score_global === 'number') ? result.score_global : null;
  const sgColor = scoreColor(sg);
  const sgGrade = scoreGrade(sg);
  // SVG arc gauge
  const radius = 36;
  const circ   = 2 * Math.PI * radius;
  const pct    = sg !== null ? Math.round((sg / 10) * 100) : 0;
  const dash   = (pct / 100) * circ;
  const scoreGlobalHTML = sg !== null ? `
    <div class="kva-score-global">
      <div class="kva-sg__gauge-wrap">
        <svg class="kva-sg__svg" viewBox="0 0 88 88" width="88" height="88">
          <circle cx="44" cy="44" r="${radius}" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="6"/>
          <circle cx="44" cy="44" r="${radius}" fill="none"
            stroke="${sgColor}" stroke-width="6"
            stroke-linecap="round"
            stroke-dasharray="${dash} ${circ}"
            stroke-dashoffset="${circ * 0.25}"
            style="filter:drop-shadow(0 0 6px ${sgColor}99);transition:stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1);"
          />
        </svg>
        <div class="kva-sg__number" style="color:${sgColor}">${sg.toFixed(1)}</div>
        <div class="kva-sg__denom">/10</div>
      </div>
      <div class="kva-sg__right">
        <div class="kva-sg__label">${lbl.scoreGlobal}</div>
        <div class="kva-sg__grade" style="color:${sgColor}">${sgGrade}</div>
        <div class="kva-sg__sublabel">${lbl.scoreLabel(sg)}</div>
      </div>
    </div>` : '';

  // ── Zonas ─────────────────────────────────────────────────
  const zonaEntries = [
    { emoji: '🏔️', key: 'tzone',    label: 'T-Zone'   },
    { emoji: '🌸', key: 'mejillas', label: idioma === 'es' ? 'Mejillas' : 'Cheeks' },
    { emoji: '👁️', key: 'ojos',    label: idioma === 'es' ? 'Contorno ojos' : 'Eye area' },
    { emoji: '💋', key: 'boca',     label: idioma === 'es' ? 'Contorno boca' : 'Lip area' },
  ];
  const zonasHTML = zonaEntries.map(z => {
    const val = zona[z.key] || '—';
    return `<div class="kva-zone">
      <span class="kva-zone__emoji">${z.emoji}</span>
      <div class="kva-zone__info">
        <span class="kva-zone__label">${z.label}</span>
        <span class="kva-zone__val">${val}</span>
      </div>
    </div>`;
  }).join('');

  // ── Dimensiones ───────────────────────────────────────────
  const dimKeys = ['hidratacion','barrera','sebum','pigmentacion','textura','circulacion','firmeza','microbioma'];
  const dimHTML = dimKeys.map(key => {
    const d = dim[key];
    if (!d) return '';
    const score = d.score;
    const color = scoreColor(score);
    const icon  = lbl.dim_icons[key] || '';
    const scoreDisplay = (score !== null && score !== undefined) ? score + '/10' : '—';
    return `<div class="kva-dim">
      <div class="kva-dim__top">
        <span class="kva-dim__name"><span class="kva-dim__icon">${icon}</span>${lbl.dim_labels[key] || key}</span>
        <span class="kva-dim__score" style="color:${color}">${scoreDisplay}</span>
      </div>
      ${scoreBar(score)}
      ${d.label ? `<div class="kva-dim__label">${d.label}</div>` : ''}
      ${d.detalle ? `<div class="kva-dim__detail">${d.detalle}</div>` : ''}
    </div>`;
  }).join('');

  // ── Puntos críticos ───────────────────────────────────────
  const ptsHTML = pts.length
    ? `<div class="kva-section">
        <div class="kva-section__title">${lbl.criticos}</div>
        <ul class="kva-findings">
          ${pts.map(p => `<li class="kva-finding"><span class="kva-finding__arrow">→</span><span>${p}</span></li>`).join('')}
        </ul>
      </div>` : '';

  // ── Ingredientes (hasta 4) ─────────────────────────────────
  // El Worker puede devolver ingredientes como:
  //   A) Array de objetos: [{nombre, razon}, ...]
  //   B) Array de strings: ["Niacinamide: Helps reduce...", ...]
  // Normalizamos ambos formatos antes de renderizar.
  const ingsNormalizados = ings.slice(0, 4).map(ing => {
    if (typeof ing === 'string') {
      // Intentar split en ': ' para separar nombre de razón
      const colonIdx = ing.indexOf(':');
      if (colonIdx > 0) {
        return { nombre: ing.slice(0, colonIdx).trim(), razon: ing.slice(colonIdx + 1).trim() };
      }
      return { nombre: ing, razon: '' };
    }
    // Objeto estándar {nombre, razon}
    return { nombre: ing.nombre || ing.name || '', razon: ing.razon || ing.reason || '' };
  });

  const ingsHTML = ingsNormalizados.length
    ? `<div class="kva-section">
        <div class="kva-section__title">${lbl.ingredientes}</div>
        <div class="kva-ingredients">
          ${ingsNormalizados.map((ing, idx) => `
            <div class="kva-ingredient">
              <span class="kva-ingredient__num">${idx + 1}</span>
              <div class="kva-ingredient__body">
                <span class="kva-ingredient__name">${ing.nombre}</span>
                ${ing.razon ? `<span class="kva-ingredient__reason">${ing.razon}</span>` : ''}
              </div>
            </div>`).join('')}
        </div>
      </div>` : '';

  // ── Protocolo urgente ─────────────────────────────────────
  const protHTML = result.protocolo_urgente
    ? `<div class="kva-protocolo">
        <div class="kva-protocolo__label">⚡ ${lbl.protocolo}</div>
        <p class="kva-protocolo__text">${result.protocolo_urgente}</p>
      </div>` : '';

  // ── Meta (confirmación perfil) ────────────────────────────
  // Edad biológica eliminada del chat — puede romper el entusiasmo.
  // Se conserva en el Skin Report por email donde el contexto es apropiado.
  const edadBioHTML = ''; // oculta en el chat, aparece en el reporte email

  const perfilOk = result.confirmacion_perfil;
  // ajuste_perfil puede ser un objeto {nuevo_perfil_id:...} o un string — normalizar
  const ajustePerfilStr = (function() {
    const a = result.ajuste_perfil;
    if (!a || a === 'null') return '';
    if (typeof a === 'string') return a;
    if (typeof a === 'object') return a.nuevo_perfil_id || a.perfil_id || JSON.stringify(a);
    return String(a);
  })();

  const perfilHTML = `<div class="kva-meta__item">
      <span class="kva-meta__icon">${perfilOk ? '✅' : '⚠️'}</span>
      <div class="kva-meta__body">
        <span class="kva-meta__key">${perfilOk ? lbl.perfil : lbl.perfilNo}</span>
        ${!perfilOk && ajustePerfilStr
          ? `<span class="kva-meta__val">${ajustePerfilStr}</span>` : ''}
      </div>
    </div>`;

  const metaHTML = (edadBioHTML || perfilHTML)
    ? `<div class="kva-meta">${edadBioHTML}${perfilHTML}</div>` : '';

  // ── Ensamblar ─────────────────────────────────────────────
  const wrapper = document.createElement('div');
  wrapper.className = 'kva-card';
  wrapper.innerHTML = `
    <div class="kva-card__header">
      <span class="kva-card__badge">🔬 KOI</span>
      <span class="kva-card__title">${lbl.titulo}</span>
    </div>

    ${scoreGlobalHTML}

    <div class="kva-section kva-section--zones">
      <div class="kva-section__title">${lbl.zonas}</div>
      <div class="kva-zones-grid">${zonasHTML}</div>
    </div>

    <div class="kva-section">
      <div class="kva-section__title">${lbl.dimensiones}</div>
      <div class="kva-dims-grid">${dimHTML}</div>
    </div>

    ${ptsHTML}
    ${ingsHTML}
    ${protHTML}
    ${metaHTML}
  `;

  return wrapper;
}

/* ── Animar entrada de la card con stagger ── */
function _animarCardEntrada(cardEl) {
  if (!cardEl) return;
  cardEl.style.opacity = '0';
  cardEl.style.transform = 'translateY(16px)';
  cardEl.style.transition = 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34,1.56,0.64,1)';

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      cardEl.style.opacity = '1';
      cardEl.style.transform = 'translateY(0)';
    });
  });

  // Animar hijos con stagger
  const children = cardEl.querySelectorAll('.kva-section, .kva-protocolo, .kva-meta');
  children.forEach((child, i) => {
    child.style.opacity = '0';
    child.style.transform = 'translateY(8px)';
    child.style.transition = `opacity 0.4s ease ${0.15 + i * 0.08}s, transform 0.4s ease ${0.15 + i * 0.08}s`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        child.style.opacity = '1';
        child.style.transform = 'translateY(0)';
      });
    });
  });
}

/* ══════════════════════════════════════════════════════════
   ESCUCHAR EVENTO GLOBAL de KOI Vision (alternativo al callback)
   ANTI-DUPLICADO: _visionResultHandled bloquea la segunda ejecución
   sin importar qué ruta llega primero (callback o evento global).
   NO se resetea automáticamente — se resetea solo cuando
   manejarResultadoVision termina completamente (al final del async).
   ══════════════════════════════════════════════════════════ */
let _visionResultHandled = false;
function _handleVisionResult(e) {
  if (_visionResultHandled) return;
  _visionResultHandled = true;
  if (e.detail) {
    manejarResultadoVision(e.detail).finally(function() {
      // Resetear SOLO cuando el flujo async terminó completamente
      // (no antes — evita race condition con el callback de koiVision)
      setTimeout(function() { _visionResultHandled = false; }, 500);
    });
  }
}
window.addEventListener('koi-vision-result', _handleVisionResult);

async function enviarDesdeChip (texto) {
    // ── Chips de bifurcación "The Reveal" ─────────────────────
    // Se interceptan aquí antes de ir al Worker — tienen lógica
    // propia que no debe pasar por GPT-4o
    if (KOI_STATE.revealPhase === 'insight' && esChipReveal(texto)) {
      agregarMensaje('user', texto);
      await manejarChipReveal(texto);
      return;
    }

    // ── Chips post-cámara: analizar rostro o ver rutina ───────
    if (KOI_STATE.revealPhase === 'camara' && esChipPostCamara(texto)) {
      agregarMensaje('user', texto);
      await manejarChipPostCamara(texto);
      return;
    }

    // ── Chip post-visión: revelar rutina después del análisis ──
    // BYPASS: ir directamente a productos sin pasar por revelarRutinaConKOI()
    // (el diálogo de 3 párrafos + botón era redundante y creaba fricción extra)
    if (KOI_STATE.revealPhase === 'post_vision' && esChipPostVision(texto)) {
      agregarMensaje('user', texto);
      ocultarChips();
      KOI_STATE.revealPhase = 'revealed';
      setInputAreaVisible(false);

      // 1. Scroll hacia la sección de productos
      const rutinaElPV = document.getElementById('shatokb-resultado') ||
                         document.getElementById('stk-reveal-section');
      if (rutinaElPV) rutinaElPV.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // 2. Revelar productos
      setTimeout(() => {
        if (typeof window.shatokbRevelarProductos === 'function') {
          window.shatokbRevelarProductos();
        }
      }, 300);

      // 3. Restaurar input + chips de bienvenida + mini cart bar
      setTimeout(() => {
        setInputAreaVisible(true);
        setInputHabilitado(true);
        mostrarChips('bienvenida');
        if (typeof window.shatokbMostrarMiniCartBar === 'function') {
          window.shatokbMostrarMiniCartBar();
        }
      }, 1800);

      return;
    }

    // ── Chips normales → enviar al Worker como mensaje ────────
    const input = document.getElementById('koi-input');
    if (input) {
      input.value = texto;
      await enviarMensajeUsuario();
    }
  }

  // manejarResultadoVision está definida más arriba (línea ~1725) con el flujo completo:
  // card de análisis dimensional, enriquecimiento de respuestas con scores reales,
  // reasignación de perfil si la foto lo indica, etc.
  // ⚠️ NO re-definir aquí — la segunda definición sobrescribiría la buena.

  /* ── Detecta chip post-visión (después del análisis) ────── */
  function esChipPostVision (texto) {
    const t = texto.toLowerCase();
    return t.includes('elegidos para mí') || t.includes('elegidos para mi') ||
           t.includes('chosen for me')    || t.includes('choisis pour moi') ||
           t.includes('escolhidos para')  || t.includes('ausgewählten')     ||
           t.includes('scelti per me')    || t.includes('muéstrame')        ||
           t.includes('show me my routine') || t.includes('routine now')    ||
           t.includes('ahora');
  }

  /* ── Detecta chips post-cámara ──────────────────────────── */
  function esChipPostCamara (texto) {
    const t = texto.toLowerCase();
    // "📸 Sí, analiza mi rostro" / "📸 Yes, analyze my face" / etc.
    // "✨ Ver mi rutina ya" / "✨ Show me my routine" / etc.
    return (t.includes('analiza') && t.includes('rostro')) ||
           (t.includes('analyze') && t.includes('face'))  ||
           (t.includes('analyser') && t.includes('visage')) ||
           (t.includes('analisar') && t.includes('rosto')) ||
           (t.includes('analysier') && t.includes('gesicht')) ||
           (t.includes('analizza') && t.includes('viso'))  ||
           (t.includes('ver mi rutina ya'))                ||
           (t.includes('show me my routine'))              ||
           (t.includes('voir ma routine'))                 ||
           (t.includes('ver minha rotina'))                ||
           (t.includes('routine jetzt'))                   ||
           (t.includes('mostrami la mia routine'));
  }

  /* ── Maneja chips post-cámara ───────────────────────────── */
  async function manejarChipPostCamara (texto) {
    const idioma = detectarIdioma();
    ocultarChips();

    // ── Sprint 2: Chip "📸 Analyze my skin" → abrir KOI Vision ──
    const esAnalisis = texto.startsWith('📸') || texto.includes('📸') ||
                       texto.toLowerCase().includes('rostro') ||
                       texto.toLowerCase().includes('face')   ||
                       texto.toLowerCase().includes('visage') ||
                       texto.toLowerCase().includes('rosto')  ||
                       texto.toLowerCase().includes('gesicht')||
                       texto.toLowerCase().includes('viso');

    if (esAnalisis && typeof window.koiVision !== 'undefined') {
      // ── KOI Vision está disponible — abrir el módulo de cámara ──
      // Exponer la URL del Worker al módulo de visión
      window.KOI_VISION_WORKER_URL = KOI_CONFIG.workerUrl.replace('/chat', '/vision');

      // Registrar el callback ANTES de abrir (el modal lo llama al cerrar).
      // Si _visionResultHandled ya es true, el evento global llegó primero — ignorar.
      // Si es false, marcarlo true y ejecutar. El reset lo hace manejarResultadoVision.
      window.koiVision.onResultado(function(data) {
        if (_visionResultHandled) return;
        _visionResultHandled = true;
        manejarResultadoVision(data).finally(function() {
          // Resetear solo cuando el flujo async terminó completamente
          setTimeout(function() { _visionResultHandled = false; }, 500);
        });
      });

      // Fallback alternativo (si el usuario cancela la cámara)
      window.koiVisionAlternativo = function() {
        // Ir directo al flujo de reveal
        KOI_STATE.revealPhase = 'email';
        manejarChipDescubrirRutina(idioma);
      };

      // Abrir el modal de cámara con el contexto del quiz
      window.koiVision.abrir(KOI_STATE.contexto);
      return;
    }

    if (esAnalisis) {
      // ── Coming soon — pero con entusiasmo, no disculpa
      const msgs = {
        es: `Me encanta que quieras ir más lejos 📸\n\nEl módulo de análisis facial está en construcción ahora mismo — lo estamos entrenando con miles de tipos de piel para que sea realmente preciso, no solo bonito.\n\nMientras tanto, tu rutina ya está lista y es sólida. ¿La vemos?`,
        en: `Love that you want to go deeper 📸\n\nThe facial analysis module is being built right now — we're training it on thousands of skin types to make it genuinely accurate, not just pretty.\n\nIn the meantime, your routine is ready and it's solid. Want to see it?`,
        fr: `J'adore que vous vouliez aller plus loin 📸\n\nLe module d'analyse faciale est en cours de construction — nous l'entraînons sur des milliers de types de peau pour qu'il soit vraiment précis.\n\nEn attendant, votre routine est prête. On y va ?`,
        pt: `Adoro que queira ir mais fundo 📸\n\nO módulo de análise facial está sendo desenvolvido agora — estamos treinando com milhares de tipos de pele para ser genuinamente preciso.\n\nEnquanto isso, sua rotina está pronta. Quer ver?`,
        de: `Ich liebe es, dass du tiefer gehen möchtest 📸\n\nDas Gesichtsanalyse-Modul wird gerade gebaut — wir trainieren es mit tausenden Hauttypen für echte Genauigkeit.\n\nIn der Zwischenzeit ist deine Routine fertig. Sehen wir sie uns an?`,
        it: `Adoro che tu voglia andare più in profondità 📸\n\nIl modulo di analisi facciale è in costruzione — lo stiamo addestrando su migliaia di tipi di pelle per essere davvero preciso.\n\nNel frattempo, la tua routine è pronta. La vediamo?`,
      };
      const msg    = msgs[idioma] || msgs['en'];
      const textEl = agregarMensaje('koi', '');
      if (textEl) await escribirConEfecto(textEl, msg);
      KOI_STATE.historial.push({ role: 'assistant', content: msg });
      guardarHistorialLocal();
      // Ofrecer reveal como único siguiente paso
      setTimeout(() => mostrarChips('reveal'), 400);

    } else {
      // ── Usuario elige ver rutina directamente desde chip post-cámara
      // BYPASS: ir directamente a productos (mismo flujo que post_vision)
      KOI_STATE.revealPhase = 'revealed';
      setInputAreaVisible(false);

      // 1. Scroll hacia la sección de productos
      const rutinaElPC = document.getElementById('shatokb-resultado') ||
                         document.getElementById('stk-reveal-section');
      if (rutinaElPC) rutinaElPC.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // 2. Revelar productos
      setTimeout(() => {
        if (typeof window.shatokbRevelarProductos === 'function') {
          window.shatokbRevelarProductos();
        }
      }, 300);

      // 3. Restaurar input + chips de bienvenida + mini cart bar
      setTimeout(() => {
        setInputAreaVisible(true);
        setInputHabilitado(true);
        mostrarChips('bienvenida');
        if (typeof window.shatokbMostrarMiniCartBar === 'function') {
          window.shatokbMostrarMiniCartBar();
        }
      }, 1800);
    }
  }

  function setInputHabilitado (habilitado) {
    const input   = document.getElementById('koi-input');
    const sendBtn = document.getElementById('koi-send-btn');
    if (input)   input.disabled   = !habilitado;
    if (sendBtn) sendBtn.disabled = !habilitado;
  }

  // Oculta/muestra el área completa del input (barra + botón enviar).
  // Usar cuando no tiene sentido que el usuario escriba todavía
  // (ej: antes de ver la rutina). Más claro que deshabilitar en gris.
  function setInputAreaVisible (visible) {
    const area = document.querySelector('.koi-input-area');
    if (!area) return;
    if (visible) {
      area.classList.remove('koi-input-area--hidden');
    } else {
      area.classList.add('koi-input-area--hidden');
    }
  }

  /* ══════════════════════════════════════════════════════════
     LLAMADA AL CLOUDFLARE WORKER (→ OpenAI GPT-4o)
     ══════════════════════════════════════════════════════════ */
  async function obtenerRespuestaKOI (preguntaUsuario) {
    mostrarTyping();

    try {
      const payload = {
        mensaje:   preguntaUsuario,
        historial: KOI_STATE.historial.slice(-KOI_CONFIG.maxHistory),
        contexto:  construirContexto(),
      };

      const response = await fetch(KOI_CONFIG.workerUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const respuesta = data.respuesta || data.content || '';

      ocultarTyping();

      // Renderizar respuesta con efecto de escritura
      const textEl = agregarMensaje('koi', '');
      if (textEl && respuesta) {
        await escribirConEfecto(textEl, respuesta);
      }

      // Guardar en historial
      KOI_STATE.historial.push({ role: 'assistant', content: respuesta });
      guardarHistorialLocal();

      // Mostrar chips de objeciones si el historial es largo
      if (KOI_STATE.msgCount >= 4) {
        setTimeout(() => mostrarChips('objeciones'), 600);
      }

    } catch (error) {
      console.error('[KOI] Error contacting the Worker:', error);
      ocultarTyping();

      // Mensaje de fallback amigable
      const textEl = agregarMensaje('koi', '');
      if (textEl) {
        await escribirConEfecto(
          textEl,
          "I ran into a brief technical issue — could you repeat your question? I want to make sure I give you a complete answer."
        );
      }
    }
  }

  /* ══════════════════════════════════════════════════════════
     CONSTRUCCIÓN DEL CONTEXTO PARA EL WORKER
     ══════════════════════════════════════════════════════════ */
  function construirContexto () {
    const ctx = KOI_STATE.contexto;
    if (!ctx) return {};

    // Serializar productos seleccionados
    const productosStr = (ctx.productos || [])
      .map(p => `- ${p.nombre} ($${p.precio}) — Paso: ${p.paso || 'N/A'}`)
      .join('\n');

    // Serializar rutina
    const rutinaAM = (ctx.rutinaAM || []).join(' → ');
    const rutinaPM = (ctx.rutinaPM || []).join(' → ');

    // ── Incluir análisis fotográfico real si existe ───────────
    // visionResult viene del Worker /vision (GPT-4o Vision).
    // Se pasa al Worker /chat para que KOI lo use en TODAS las
    // respuestas posteriores — nunca mensajes genéricos.
    const vision = KOI_STATE.visionResult || null;
    let visionResumen = '';
    if (vision && vision.dimensiones) {
      const dim = vision.dimensiones;
      const scores = Object.entries(dim)
        .map(([k, v]) => `${k}: ${v?.score ?? '?'}/10`)
        .join(', ');
      const edad = vision.edad_biologica_estimada
        ? `Edad biológica estimada: ${vision.edad_biologica_estimada} años. `
        : '';
      const criticos = (vision.puntos_criticos || []).join('; ');
      const ings = (vision.ingredientes_prioritarios || []).join(', ');
      visionResumen = `${edad}Scores dimensionales (0-10): ${scores}.` +
        (criticos ? ` Hallazgos críticos: ${criticos}.` : '') +
        (ings     ? ` Activos prioritarios: ${ings}.`   : '');
    }

    return {
      perfil_id:          ctx.perfil?.id          || '',
      perfil_nombre:      ctx.perfil?.nombre       || '',
      perfil_descripcion: ctx.perfil?.descripcion  || '',
      caracteristicas:    (ctx.perfil?.tags || []).join(', '),
      rutina_am:          rutinaAM,
      rutina_pm:          rutinaPM,
      productos:          productosStr,
      presupuesto:        ctx.presupuesto          || '',
      experiencia:        ctx.experiencia          || '',
      total_carrito:      ctx.totalCarrito         || 0,
      // ── Análisis fotográfico real de la piel ─────────────
      vision_analizado:   vision ? true : false,
      vision_resumen:     visionResumen,
      vision_score_global: vision?.score_global    || null,
      vision_mensaje:     vision?.mensaje_reveal   || '',
    };
  }

  /* ══════════════════════════════════════════════════════════
     EFECTO DE ESCRITURA (simula streaming humanizado)
     ══════════════════════════════════════════════════════════ */
  async function escribirConEfecto (elemento, texto) {
    // Convertir markdown básico a HTML
    const html = markdownBasico(texto);
    elemento.innerHTML = '';

    // Crear contenedor temporal para parsear el HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const textoPlano = temp.textContent || temp.innerText || '';

    // Cursor parpadeante
    const cursor = document.createElement('span');
    cursor.className = 'koi-cursor';
    elemento.appendChild(cursor);

    let i = 0;
    const velocidad = textoPlano.length > 200 ? 12 : 18; // ms por carácter

    await new Promise(resolve => {
      const interval = setInterval(() => {
        if (i >= textoPlano.length) {
          clearInterval(interval);
          // Reemplazar texto plano con HTML formateado
          elemento.innerHTML = html;
          resolve();
          return;
        }

        // Escribir letra por letra
        cursor.remove();
        elemento.innerHTML = markdownBasico(textoPlano.slice(0, i + 1));
        elemento.appendChild(cursor);
        scrollAlFinal();
        i++;
      }, velocidad);
    });

    // Quitar cursor al terminar
    const cur = elemento.querySelector('.koi-cursor');
    if (cur) cur.remove();
  }

  /* ── Markdown básico → HTML ───────────────────────────── */
  function markdownBasico (texto) {
    return texto
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g,     '<em>$1</em>')
      .replace(/\n/g,            '<br>')
      .replace(/📍|✨|🌸|💆|☀️|🌙|✅|❌|🔒|💡/g, match => match);
  }

  /* ── Utilidad: pausa ────────────────────────────────────── */
  function pausa (ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /* ══════════════════════════════════════════════════════════
     INTEGRACIÓN CON shatokb-quiz.js
     Escucha el evento custom que dispara el quiz cuando
     muestra el resultado, y arranca KOI automáticamente.

     GUARD ANTI-DUPLICACIÓN:
     shatokbIniciarKOI ya tiene "if (KOI_STATE.isReady) return" como
     primera línea — eso basta para bloquear cualquier segunda llamada,
     sin importar la ruta (CustomEvent o llamada directa desde quiz.js).
     ══════════════════════════════════════════════════════════ */

  // KOI NO se inicia automáticamente.
  // Solo arranca cuando el usuario hace clic en "Open my analysis →"
  // que llama a shatokbScrollAKOI() → shatokbIniciarKOI().

  /* ══════════════════════════════════════════════════════════
     ❓ BOTÓN POR PRODUCTO — API pública
     Llamado por shatokb-quiz.js al hacer clic en el botón ❓
     de cada tarjeta de producto en la rutina.

     @param {string} nombreProducto  Nombre del producto
     @param {string} paso            Nombre del paso (ej: "Cleanser AM/PM")
     @param {string} precio          Precio formateado (ej: "$28.00")
     ══════════════════════════════════════════════════════════ */
  window.shatokbPreguntarProducto = function (nombreProducto, paso, precio) {
    const idioma = detectarIdioma();

    // ── 1. Si KOI aún no está listo, intentar iniciarlo ───────
    if (!KOI_STATE.isReady) {
      const ctx = obtenerContextoLocal();
      if (ctx && typeof window.shatokbIniciarKOI === 'function') {
        window.shatokbIniciarKOI(ctx);
        // Esperar a que KOI esté listo y reintentar
        setTimeout(() => window.shatokbPreguntarProducto(nombreProducto, paso, precio), 2800);
      }
      return;
    }

    // ── 2. Preguntas localizadas ───────────────────────────────
    const preguntas = {
      es: `¿Por qué elegiste "${nombreProducto}" para mi perfil exactamente? ¿Qué hace por mi tipo de piel específico?`,
      en: `Why did you choose "${nombreProducto}" specifically for my profile? What does it do for my skin type?`,
      fr: `Pourquoi avez-vous choisi "${nombreProducto}" spécifiquement pour mon profil ? Qu'est-ce qu'il fait pour mon type de peau ?`,
      pt: `Por que você escolheu "${nombreProducto}" especificamente para o meu perfil? O que ele faz pelo meu tipo de pele?`,
      de: `Warum hast du "${nombreProducto}" speziell für mein Profil gewählt? Was macht es für meinen Hauttyp?`,
      it: `Perché hai scelto "${nombreProducto}" specificatamente per il mio profilo? Cosa fa per il mio tipo di pelle?`,
    };
    const pregunta = preguntas[idioma] || preguntas['en'];

    // ── 3. Hacer scroll al panel KOI (centrado en desktop) ───────────────
    const panel = document.getElementById('shatokb-koi-wrapper');
    if (panel) {
      var r      = panel.getBoundingClientRect();
      var absTop = r.top + window.pageYOffset;
      window.scrollTo({ top: Math.max(0, absTop - 80), behavior: 'smooth' });
    }

    // ── 4. Inyectar y enviar la pregunta ──────────────────────
    // Pequeño delay para que el scroll termine antes de que KOI responda
    setTimeout(function () {
      const input = document.getElementById('koi-input');
      if (input && !KOI_STATE.isTyping) {
        input.value = pregunta;
        enviarMensajeUsuario();
      }
    }, panel ? 400 : 0);
  };

  /* ══════════════════════════════════════════════════════════
     INTERCEPTOR DE CARRITO
     Llamado por shatokbAddAllToCart() antes de proceder.
     Si el email ya fue capturado → ejecuta el callback directo.
     Si no → KOI pide el email con el contexto de "te envío
     tu Skin Report" y luego ejecuta el callback.
     ══════════════════════════════════════════════════════════ */
  window.shatokbInterceptarCarrito = function (callbackProcederAlCarrito) {
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // GATE 3 — ACTIVO — 2026-07-27 v92 — Modal autosuficiente
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    console.log('%c[KOI Gate3 v92] shatokbInterceptarCarrito llamado', 'color:#ec95b8;font-size:14px;font-weight:bold');
    console.log('[KOI Gate3 v92] emailCaptured:', KOI_STATE.emailCaptured);
    console.log('[KOI Gate3 v92] localStorage email:', (function(){ try { return localStorage.getItem('shatokb_email'); } catch(_){ return 'ERROR'; } })());

    // Recuperar email de localStorage si KOI_STATE no lo tiene aún
    const emailGuardado = KOI_STATE.emailCaptured
      || (function(){ try { return localStorage.getItem('shatokb_email'); } catch(_){ return null; } })();

    if (emailGuardado && !KOI_STATE.emailCaptured) {
      KOI_STATE.emailCaptured = emailGuardado;
    }

    if (KOI_STATE.emailCaptured) {
      // Email ya conocido — enviar reporte en background y proceder al carrito
      const emailYaGuardado = KOI_STATE.emailCaptured;
      enviarSkinReport(emailYaGuardado).then(token => {
        if (token) {
          console.log('%c[KOI Gate3 v92] Email conocido, reporte enviado ✅', 'color:#22c55e;font-weight:bold', token.slice(0,8) + '…');
        } else {
          console.warn('[KOI Gate3 v92] Reporte falló (email conocido) — quiz.js hará retry');
        }
      }).catch(() => {});
      callbackProcederAlCarrito();
      return;
    }

    // Sin email → mostrar el modal autosuficiente (sin skip)
    console.log('%c[KOI Gate3 v92] Sin email — llamando inyectarEmailGateCarrito()', 'color:#f59e0b;font-weight:bold');
    inyectarEmailGateCarrito(callbackProcederAlCarrito);
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // GATE 3 v92 — MODAL 100% AUTOSUFICIENTE
  // NO depende del panel KOI ni de ningún otro elemento del DOM del chat.
  // Funciona aunque el chat no esté abierto, no esté inicializado, o no exista.
  // Se inyecta directamente en document.body con position:fixed.
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  function inyectarEmailGateCarrito (callbackProcederAlCarrito) {
    // Evitar duplicados — check por la card directamente (no el overlay que dependía del panel)
    if (document.getElementById('koi-email-gate-modal')) {
      console.warn('[KOI Gate3 v92] Modal ya existe — ignorando duplicado');
      return;
    }

    console.log('%c[KOI Gate3 v92] Inyectando modal...', 'color:#ec95b8;font-weight:bold');

    const idioma = detectarIdioma();

    const ui = {
      es: { placeholder: 'tu@email.com', btn: 'Enviar mi reporte →', note: '🔒 Solo para tu Skin Report. Sin spam.' },
      en: { placeholder: 'you@email.com', btn: 'Send my report →',    note: '🔒 Only for your Skin Report. No spam. Unsubscribe anytime.' },
      fr: { placeholder: 'vous@email.com', btn: 'Envoyer et continuer →', note: '🔒 Uniquement pour votre Skin Report.' },
      pt: { placeholder: 'voce@email.com', btn: 'Enviar e continuar →', note: '🔒 Apenas para o seu Skin Report. Sem spam.' },
      de: { placeholder: 'du@email.com',   btn: 'Senden & weiter →',   note: '🔒 Nur für deinen Skin Report. Kein Spam.' },
      it: { placeholder: 'tu@email.com',   btn: 'Invia e continua →',  note: '🔒 Solo per il tuo Skin Report.' },
    }[idioma] || { placeholder: 'you@email.com', btn: 'Send my report →', note: '🔒 Only for your Skin Report. No spam.' };

    const pitchTexts = {
      es: {
        headline: '🔬 Un último paso antes del carrito',
        body: 'Preparé tu <strong>Skin Report personalizado</strong> — incluye tu análisis de piel, el por qué de cada producto que elegiste y tu <strong>rutina AM/PM paso a paso</strong>.',
        ask: '¿A dónde te lo envío? <strong style="color:#ec95b8;">Expira en 24 horas.</strong>',
      },
      en: {
        headline: '🔬 One last step before your cart',
        body: 'I built your <strong>personalized Skin Report</strong> with the exact products you chose — your skin analysis, why each product works for you, and your <strong>step-by-step AM/PM routine guide</strong>.',
        ask: 'Where should I send it? <strong style="color:#ec95b8;">It expires in 24 hours.</strong>',
      },
      fr: {
        headline: '🔬 Une dernière étape avant le panier',
        body: 'J\'ai préparé votre <strong>Skin Report personnalisé</strong> avec les produits que vous avez choisis.',
        ask: 'Où dois-je vous l\'envoyer ? <strong style="color:#ec95b8;">Il expire dans 24 heures.</strong>',
      },
      pt: {
        headline: '🔬 Último passo antes do carrinho',
        body: 'Preparei seu <strong>Skin Report personalizado</strong> com os produtos que você escolheu.',
        ask: 'Para onde devo enviar? <strong style="color:#ec95b8;">Expira em 24 horas.</strong>',
      },
      de: {
        headline: '🔬 Letzter Schritt vor dem Warenkorb',
        body: 'Ich habe deinen <strong>persönlichen Skin Report</strong> mit den Produkten erstellt, die du gewählt hast.',
        ask: 'Wohin soll ich ihn schicken? <strong style="color:#ec95b8;">Er verfällt in 24 Stunden.</strong>',
      },
      it: {
        headline: '🔬 Ultimo passaggio prima del carrello',
        body: 'Ho preparato il tuo <strong>Skin Report personalizzato</strong> con i prodotti che hai scelto.',
        ask: 'Dove devo inviarlo? <strong style="color:#ec95b8;">Scade in 24 ore.</strong>',
      },
    };
    const pitch = pitchTexts[idioma] || pitchTexts['en'];

    // ── Inyectar CSS del modal (solo una vez) ──────────────────────────────
    if (!document.getElementById('koi-gate3-style')) {
      const st = document.createElement('style');
      st.id = 'koi-gate3-style';
      st.textContent = [
        '#koi-gate3-backdrop{position:fixed!important;inset:0!important;z-index:2147483640!important;background:rgba(0,0,0,0.72)!important;backdrop-filter:blur(2px)!important;-webkit-backdrop-filter:blur(2px)!important;}',
        '#koi-email-gate-modal{position:fixed!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)!important;z-index:2147483647!important;width:calc(100vw - 32px)!important;max-width:420px!important;max-height:90dvh!important;background:#1c181a!important;border:1px solid rgba(236,149,184,0.4)!important;border-radius:20px!important;box-shadow:0 24px 80px rgba(0,0,0,0.85),0 0 0 1px rgba(236,149,184,0.12)!important;overflow:hidden!important;display:flex!important;flex-direction:column!important;box-sizing:border-box!important;font-family:Arimo,Arial,sans-serif!important;}',
        '#koi-gate3-body{flex:1 1 auto!important;overflow-y:auto!important;-webkit-overflow-scrolling:touch!important;padding:28px 26px 20px!important;display:flex!important;flex-direction:column!important;gap:18px!important;box-sizing:border-box!important;}',
        '#koi-gate3-koi-row{display:flex!important;align-items:center!important;gap:10px!important;}',
        '#koi-gate3-avatar{width:40px!important;height:40px!important;border-radius:50%!important;background:linear-gradient(135deg,#b83280,#ec95b8)!important;display:flex!important;align-items:center!important;justify-content:center!important;font-size:20px!important;flex-shrink:0!important;}',
        '#koi-gate3-name{font-family:Prompt,Arial,sans-serif!important;font-size:13px!important;font-weight:700!important;color:#ec95b8!important;letter-spacing:0.05em!important;}',
        '#koi-gate3-bubble{background:rgba(236,149,184,0.07)!important;border-radius:0 14px 14px 14px!important;border:1px solid rgba(236,149,184,0.15)!important;padding:16px 18px!important;}',
        '#koi-gate3-headline{font-family:Prompt,Arial,sans-serif!important;font-size:17px!important;font-weight:700!important;color:#f5e6ef!important;margin:0 0 10px!important;line-height:1.3!important;}',
        '#koi-gate3-body-text{font-size:14px!important;color:rgba(240,220,230,0.85)!important;margin:0 0 10px!important;line-height:1.55!important;}',
        '#koi-gate3-ask{font-size:14px!important;color:rgba(240,220,230,0.85)!important;margin:0!important;line-height:1.55!important;}',
        '#koi-gate3-form{display:flex!important;flex-direction:column!important;gap:10px!important;}',
        '#koi-gate3-input{width:100%!important;box-sizing:border-box!important;background:#13100f!important;border:1.5px solid rgba(236,149,184,0.3)!important;border-radius:10px!important;padding:14px 16px!important;font-size:16px!important;color:#f5e6ef!important;outline:none!important;font-family:Arimo,Arial,sans-serif!important;transition:border-color 0.2s!important;}',
        '#koi-gate3-input:focus{border-color:rgba(236,149,184,0.7)!important;}',
        '#koi-gate3-input.error{border-color:#f87171!important;animation:koi-shake 0.3s ease!important;}',
        '#koi-gate3-btn{width:100%!important;box-sizing:border-box!important;background:linear-gradient(135deg,#b83280,#ec95b8)!important;border:none!important;border-radius:10px!important;padding:15px!important;font-size:16px!important;font-weight:700!important;color:#fff!important;cursor:pointer!important;font-family:Prompt,Arial,sans-serif!important;letter-spacing:0.03em!important;transition:opacity 0.2s!important;}',
        '#koi-gate3-btn:hover{opacity:0.9!important;}',
        '#koi-gate3-btn:disabled{opacity:0.6!important;cursor:not-allowed!important;}',
        '#koi-gate3-footer{flex-shrink:0!important;padding:12px 26px 20px!important;border-top:1px solid rgba(255,255,255,0.07)!important;text-align:center!important;}',
        '#koi-gate3-note{font-size:11px!important;color:rgba(255,255,255,0.38)!important;line-height:1.4!important;}',
        '@keyframes koi-shake{0%,100%{transform:translate(-50%,-50%) translateX(0)!important}25%{transform:translate(-50%,-50%) translateX(-6px)!important}75%{transform:translate(-50%,-50%) translateX(6px)!important}}',
        '@keyframes koi-gate3-in{from{opacity:0;transform:translate(-50%,-50%) scale(0.94)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}',
        '#koi-email-gate-modal{animation:koi-gate3-in 0.25s ease!important;}',
      ].join('\n');
      document.head.appendChild(st);
    }

    // ── Bloquear scroll ────────────────────────────────────────────────────
    const _bodyScrollPrev = document.body.style.overflow;
    const _htmlScrollPrev = document.documentElement.style.overflow;
    document.body.style.overflow            = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // ── Backdrop oscuro ────────────────────────────────────────────────────
    const backdrop = document.createElement('div');
    backdrop.id = 'koi-gate3-backdrop';
    document.body.appendChild(backdrop);

    // ── Card del modal ─────────────────────────────────────────────────────
    const card = document.createElement('div');
    card.id = 'koi-email-gate-modal';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', pitch.headline);
    card.innerHTML = '<div id="koi-gate3-body">' +
      '<div id="koi-gate3-koi-row">' +
        '<div id="koi-gate3-avatar">\uD83C\uDF38</div>' +
        '<div id="koi-gate3-name">KOI</div>' +
      '</div>' +
      '<div id="koi-gate3-bubble">' +
        '<p id="koi-gate3-headline">' + pitch.headline + '</p>' +
        '<p id="koi-gate3-body-text">' + pitch.body + '</p>' +
        '<p id="koi-gate3-ask">' + pitch.ask + '</p>' +
      '</div>' +
      '<div id="koi-gate3-form">' +
        '<input type="email" id="koi-gate3-input" placeholder="' + ui.placeholder + '" autocomplete="email" inputmode="email" />' +
        '<button id="koi-gate3-btn">' + ui.btn + '</button>' +
      '</div>' +
    '</div>' +
    '<div id="koi-gate3-footer">' +
      '<span id="koi-gate3-note">' + ui.note + '</span>' +
    '</div>';

    document.body.appendChild(card);
    console.log('%c[KOI Gate3 v92] Modal inyectado en DOM ✅', 'color:#22c55e;font-weight:bold');

    // Focus al input (solo desktop — no abrir teclado en mobile automáticamente)
    setTimeout(function() {
      var inp = document.getElementById('koi-gate3-input');
      if (inp && window.innerWidth > 900) inp.focus();
    }, 300);

    // ── Cerrar modal ────────────────────────────────────────────────────────
    function _cerrarModal() {
      // Restaurar scroll
      document.body.style.overflow            = _bodyScrollPrev;
      document.documentElement.style.overflow = _htmlScrollPrev;
      // Quitar backdrop y card
      if (backdrop.parentNode) backdrop.remove();
      if (card.parentNode)     card.remove();
      // Quitar CSS solo si no hay otro modal abierto
      var st = document.getElementById('koi-gate3-style');
      if (st) st.remove();
    }

    // ── Confirmar email ─────────────────────────────────────────────────────
    async function confirmarEmailCarrito () {
      var inp = document.getElementById('koi-gate3-input');
      if (!inp) return;
      var email = inp.value.trim();

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        inp.classList.add('error');
        setTimeout(function() { inp.classList.remove('error'); }, 800);
        return;
      }

      // Deshabilitar botón para evitar doble clic
      var btnEl = document.getElementById('koi-gate3-btn');
      if (btnEl) { btnEl.disabled = true; btnEl.textContent = '⏳ Un momento...'; }

      // Guardar email en estado y localStorage
      KOI_STATE.emailCaptured = email;
      try { localStorage.setItem('shatokb_email', email); } catch (_) {}

      // Enviar a Shopify /contact (best-effort — no bloqueante)
      shatokbEnviarEmailShopify(email);

      // Cerrar modal inmediatamente para buena UX
      _cerrarModal();

      // Añadir al chat si está disponible (best-effort — no bloqueante)
      try { if (typeof agregarMensaje === 'function') agregarMensaje('user', email); } catch(_) {}

      // Generar Skin Report + token (async — no bloquea el carrito visualmente)
      console.log('%c[KOI Gate3 v92] Generando Skin Report para: ' + email, 'color:#a78bfa;font-weight:bold');
      var tokenObtenido = await enviarSkinReport(email);
      if (tokenObtenido) {
        console.log('%c[KOI Gate3 v92] Token listo ✅', 'color:#22c55e;font-weight:bold', tokenObtenido.slice(0,8) + '…');
      } else {
        console.warn('[KOI Gate3 v92] Token no obtenido — quiz.js hará retry si es necesario');
      }

      // Pequeña pausa UX para que el modal se vea cerrar antes del carrito
      await new Promise(function(resolve) { setTimeout(resolve, 200); });
      callbackProcederAlCarrito();
    }

    // ── Eventos ─────────────────────────────────────────────────────────────
    var btnEl = document.getElementById('koi-gate3-btn');
    var inpEl = document.getElementById('koi-gate3-input');

    if (btnEl) btnEl.addEventListener('click', confirmarEmailCarrito);
    if (inpEl) inpEl.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') { e.preventDefault(); confirmarEmailCarrito(); }
    });
  }

  /* ══════════════════════════════════════════════════════════
     MINI BARRA DE CARRITO — dentro del chat
     Replica exactamente la barra sticky superior del quiz.

     Timing de aparición:
       La barra se llena con el precio en cuanto está disponible
       (via evento o polling), pero NO se hace visible hasta que
       el usuario haya hecho clic en "Ver mi rutina ahora".
       Así el usuario descubre primero los productos antes de
       ver el botón de compra, lo que mejora la conversión.

       window.shatokbMostrarMiniCartBar() — función pública
       llamada desde el click del botón "Ver mi rutina ahora"
       para activar la visibilidad con animación.

     Clic → llama window.shatokbAddAllToCart() exactamente
     igual que el botón de la barra superior.
     ══════════════════════════════════════════════════════════ */

  // Datos en memoria — se llenan en cuanto llega el precio,
  // aunque la barra aún no sea visible.
  var _miniCartData = { total: '', totalOrig: '', label: '', cta: '' };

  // Flag: ¿ya puede mostrarse la barra? (se activa al hacer reveal)
  var _miniCartRevealed = false;

  function _actualizarMiniCartBar (total, label, cta, totalOrig) {
    // Guardar siempre en memoria (para cuando se active el reveal)
    if (total)     _miniCartData.total     = total;
    if (totalOrig) _miniCartData.totalOrig = totalOrig;
    if (label) _miniCartData.label = label;
    if (cta)   _miniCartData.cta   = cta;

    // Solo renderizar si el reveal ya ocurrió
    if (!_miniCartRevealed) return;
    _renderizarMiniCartBar();
  }

  function _renderizarMiniCartBar () {
    const bar         = document.getElementById('koi-mini-cart-bar');
    const labelEl     = document.getElementById('koi-mini-cart-label');
    const totalEl     = document.getElementById('koi-mini-cart-total');
    const totalOrigEl = document.getElementById('koi-mini-cart-total-orig');
    const btnEl       = document.getElementById('koi-mini-cart-btn');
    if (!bar || !labelEl || !totalEl || !btnEl) return;

    const total     = _miniCartData.total;
    const totalOrig = _miniCartData.totalOrig;
    const label     = _miniCartData.label;
    const cta       = _miniCartData.cta;

    // Solo mostrar si tenemos precio
    if (!total) return;

    // ── Textos localizados según idioma del navegador ────────
    const _idioma = detectarIdioma();
    const _i18nBar = {
      es: { label: 'Total estimado de tu rutina', cta: '🛒 Añadir toda mi rutina al carrito' },
      fr: { label: 'Total estimé pour ta routine', cta: '🛒 Ajouter ma routine au panier' },
      pt: { label: 'Total estimado da sua rotina', cta: '🛒 Adicionar toda a rotina ao carrinho' },
      de: { label: 'Geschätzter Gesamt deiner Routine', cta: '🛒 Meine Routine in den Warenkorb' },
      it: { label: 'Totale stimato per la tua routine', cta: '🛒 Aggiungi la mia routine al carrello' },
    };
    const _t = _i18nBar[_idioma] || { label: 'Estimated total for your routine', cta: '🛒 Add my full routine to cart' };

    // Rellenar contenido (label/cta del quiz override si vienen, si no usar i18n)
    labelEl.textContent = label || _t.label;
    totalEl.textContent = total;   // precio con 25% OFF
    if (totalOrigEl) totalOrigEl.textContent = totalOrig || '';
    btnEl.textContent   = cta   || _t.cta;

    // ── LAYOUT BARRA: precio izq fijo, botón centrado/der ───────────────
    // Estructura: [__info (izq, absolute)] [__btn (centrado en la barra)]
    // La barra usa position:relative + flex centrado.
    // El __info se posiciona absolute a la izquierda.

    // Barra contenedora
    bar.setAttribute('style', [
      'display:flex',
      'align-items:center',
      'justify-content:center',   // botón centrado
      'position:relative',        // necesario para absolute del info
      'width:100%',
      'box-sizing:border-box',
      'background:#1c181a',
      'background-color:#1c181a',
      'border-top:none',
      'border-bottom:1px solid rgba(236,149,184,0.25)',
      'border-left:none',
      'border-right:none',
      'padding:14px 24px',
      'margin:0',
      'min-height:68px',
      'gap:0',
    ].join(';') + ';');

    // Info: label + precio — posición absoluta izquierda
    const infoEl = bar.querySelector('.koi-mini-cart-bar__info');
    if (infoEl) {
      infoEl.setAttribute('style', [
        'display:flex',
        'flex-direction:column',
        'gap:2px',
        'position:absolute',
        'left:20px',
        'top:50%',
        'transform:translateY(-50%)',
        'align-items:flex-start',
        'text-align:left',
        'pointer-events:none',
      ].join(';') + ';');
    }

    // Label "Estimated total for your routine"
    labelEl.setAttribute('style', [
      'font-family:Arimo,Arial,sans-serif',
      'font-size:11px',
      'font-weight:400',
      'color:rgba(255,255,255,0.55)',
      'white-space:nowrap',
      'line-height:1.2',
      'display:block',
      'margin:0',
      'padding:0',
      'background:transparent',
      'border:none',
    ].join(';') + ';');

    // Contenedor de precios (flex row)
    const pricesEl = bar.querySelector('.koi-mini-cart-bar__prices');
    if (pricesEl) {
      pricesEl.setAttribute('style', [
        'display:flex',
        'align-items:center',
        'gap:6px',
        'flex-wrap:wrap',
        'margin:0',
        'padding:0',
      ].join(';') + ';');
    }

    // Precio original — tachado y apagado
    if (totalOrigEl) {
      totalOrigEl.setAttribute('style', [
        'font-family:Arimo,Arial,sans-serif',
        'font-size:13px',
        'font-weight:600',
        'color:rgba(255,255,255,0.35)',
        'text-decoration:line-through',
        'line-height:1',
        'display:' + (totalOrig ? 'inline' : 'none'),
        'margin:0',
        'padding:0',
        'background:transparent',
        'border:none',
      ].join(';') + ';');
    }

    // Badge 25% OFF
    const offBadgeEl = bar.querySelector('.koi-mini-cart-bar__off-badge');
    if (offBadgeEl) {
      offBadgeEl.setAttribute('style', [
        'display:' + (totalOrig ? 'inline-flex' : 'none'),
        'align-items:center',
        'font-family:Arimo,Arial,sans-serif',
        'font-size:9px',
        'font-weight:700',
        'letter-spacing:0.04em',
        'color:#fff',
        'background:linear-gradient(135deg,#be185d,#db2777)',
        'border-radius:20px',
        'padding:2px 7px',
        'white-space:nowrap',
        'margin:0',
        'border:none',
        'box-shadow:0 1px 4px rgba(190,24,93,0.35)',
      ].join(';') + ';');
    }

    // Precio con 25% OFF — en rosa claro
    totalEl.setAttribute('style', [
      'font-family:Prompt,Arial Black,sans-serif',
      'font-size:20px',
      'font-weight:700',
      'color:#f9a8d4',
      'line-height:1',
      'display:block',
      'margin:0',
      'padding:0',
      'background:transparent',
      'border:none',
    ].join(';') + ';');

    // ── BOTÓN ROSA — estilos inline indestructibles ──────────────────────
    btnEl.setAttribute('style', [
      'background:#ec95b8',
      'background-color:#ec95b8',
      'background-image:none',
      'color:#1c181a',
      'border:none',
      'border-width:0',
      'border-style:none',
      'border-color:transparent',
      'outline:none',
      'box-shadow:0 4px 14px rgba(236,149,184,0.35)',
      'border-radius:50px',          // pill shape
      'padding:14px 32px',
      'font-family:Prompt,Arial Black,sans-serif',
      'font-size:15px',
      'font-weight:700',
      'cursor:pointer',
      'white-space:nowrap',
      'text-decoration:none',
      'display:inline-flex',
      'align-items:center',
      'justify-content:center',
      'min-height:50px',
      'line-height:1.2',
      '-webkit-appearance:none',
      'appearance:none',
      'position:relative',           // encima del info absolute
      'z-index:1',
    ].join(';') + ';');

    // hover via JS
    btnEl.onmouseenter = function() {
      this.style.backgroundColor = '#f0aac8';
      this.style.transform = 'translateY(-1px)';
    };
    btnEl.onmouseleave = function() {
      this.style.backgroundColor = '#ec95b8';
      this.style.transform = 'translateY(0)';
    };

    // Mostrar con animación
    bar.classList.add('koi-mini-cart-bar--visible');

    // Vincular clic (una sola vez)
    if (!bar.dataset.bound) {
      bar.dataset.bound = '1';
      btnEl.addEventListener('click', function () {
        _miniCartEjecutarCompra(btnEl);
      });
    }
  }

  // ── Ejecutar compra desde la mini barra ───────────────────
  // Versión robusta que no depende del botón #stk-add-btn externo
  // (que puede estar oculto o deshabilitado).
  // Flujo: interceptar email si no capturado → luego añadir al carrito.
  function _miniCartEjecutarCompra (btnEl) {
    var textoOriginal = btnEl.textContent;
    btnEl.disabled    = true;
    btnEl.textContent = '⏳ Un momento...';

    var _procederAlCarrito = function () {
      // ── Estrategia 1: leer handles directamente del DOM ──────────────────
      // Cada producto seleccionado en el quiz tiene class "selected" y
      // atributo data-handle. Esto no depende de window.shatokbState (local).
      var handles = [];
      var selectedEls = document.querySelectorAll('.stk-prod-option.selected[data-handle]');
      selectedEls.forEach(function (el) {
        var h = el.getAttribute('data-handle');
        if (h && h.trim() && handles.indexOf(h.trim()) === -1) {
          handles.push(h.trim());
        }
      });

      // ── Estrategia 2 (fallback): window.shatokbState si está expuesto ────
      if (handles.length === 0 && window.shatokbState && window.SHATOKB_CATALOGO) {
        handles = Object.values(window.shatokbState.selectedProducts || {})
          .map(function (prodId) {
            var prod = window.SHATOKB_CATALOGO.find(function (p) { return p.id === prodId; });
            return prod ? prod.handle : null;
          })
          .filter(Boolean);
      }

      // ── Con handles obtenidos, ejecutar directamente shatokbEjecutarAddToCart ──
      // Esta función global en quiz.js hace fetch a /products/handle.js
      // luego /cart/add.js y redirige a /cart. No depende de ningún botón.
      // IMPORTANTE: usamos window.shatokbEjecutarAddToCart porque koi-chat.js
      // corre dentro de una IIFE y no tiene acceso al scope global sin window.
      if (handles.length > 0 && typeof window.shatokbEjecutarAddToCart === 'function') {
        btnEl.textContent = '⏳ Añadiendo al carrito...';
        // Creamos un objeto botón proxy para recibir los cambios de texto/estado
        // sin afectar ningún elemento real del DOM externo.
        var btnProxy = {
          _el: btnEl,
          _textoOriginal: textoOriginal,
          get disabled () { return this._el.disabled; },
          set disabled (v) { this._el.disabled = v; },
          get textContent () { return this._el.textContent; },
          set textContent (v) { this._el.textContent = v; }
        };
        window.shatokbEjecutarAddToCart(handles, btnProxy);
        return;
      }

      // ── Último fallback: restaurar #stk-add-btn y llamar shatokbAddAllToCart ──
      // Solo se llega aquí si no encontramos ningún producto seleccionado en el DOM.
      if (typeof window.shatokbAddAllToCart === 'function') {
        var btnExterno = document.getElementById('stk-add-btn');
        if (!btnExterno) {
          // Crear un botón fantasma en el DOM para que shatokbAddAllToCart lo encuentre
          btnExterno = document.createElement('button');
          btnExterno.id = 'stk-add-btn';
          btnExterno.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none;overflow:hidden;';
          document.body.appendChild(btnExterno);
        }
        btnExterno.disabled    = false;
        btnExterno.textContent = '🛒 Add my full routine to cart';
        window.shatokbAddAllToCart();
      } else {
        btnEl.disabled    = false;
        btnEl.textContent = textoOriginal;
      }
    };

    // Interceptar email si aún no fue capturado
    if (typeof window.shatokbInterceptarCarrito === 'function') {
      window.shatokbInterceptarCarrito(_procederAlCarrito);
    } else {
      _procederAlCarrito();
    }
  }

  // API pública: llamada desde el click de "Ver mi rutina ahora"
  // para activar la visibilidad de la mini barra.
  window.shatokbMostrarMiniCartBar = function () {
    _miniCartRevealed = true;
    _renderizarMiniCartBar();
  };

  // Ruta 1: evento custom disparado por shatokb-quiz.js cuando actualiza la barra
  document.addEventListener('shatokb:totalBar', function (e) {
    const { total, label, cta } = e.detail || {};
    _actualizarMiniCartBar(total, label, cta);
  });

  // ── Ocultar barra sticky superior del quiz ───────────────
  // La mini barra dentro del chat la reemplaza completamente.
  // Se busca por ID exacto (#stk-total-bar) y por selectores
  // de fallback. Se oculta con transición suave al encontrarla.
  var _barraExternaEl       = null;
  var _barraExternaOculta   = false;

  function _ocultarBarraExterna () {
    if (!_barraExternaEl || _barraExternaOculta) return;
    _barraExternaOculta = true;
    _barraExternaEl.style.transition    = 'opacity 0.4s ease, transform 0.4s ease';
    _barraExternaEl.style.opacity       = '0';
    _barraExternaEl.style.transform     = 'translateY(-8px)';
    _barraExternaEl.style.pointerEvents = 'none';
    setTimeout(function () {
      if (_barraExternaEl) _barraExternaEl.style.display = 'none';
    }, 420);
  }

  // ── Polling continuo de precio ────────────────────────────
  // Lee #stk-total-amount cada 600ms — fuente de verdad directa
  // del quiz (shatokbActualizarTotal() escribe ahí).
  // NO para nunca: detecta cambios cuando el usuario cambia producto.
  // También encuentra y oculta la barra sticky externa la primera vez.
  (function _pollPrecioQuiz () {
    var _precioAnterior = '';

    setInterval(function () {

      // ── 1. Ocultar barra externa (solo la primera vez) ──────
      if (!_barraExternaOculta) {
        // Estrategia 1: ID exacto del quiz
        var barraCandidata = document.getElementById('stk-total-bar');

        // Estrategia 2: selectores de clase conocidos
        if (!barraCandidata) {
          barraCandidata = document.querySelector(
            '.stk-total-bar, .shatokb-total-bar, ' +
            '[class*="total-bar"]:not(#koi-mini-cart-bar), ' +
            '[id*="total-bar"]:not(#koi-mini-cart-bar)'
          );
        }

        // Validar que no sea nuestra propia mini barra
        if (barraCandidata &&
            barraCandidata.id !== 'koi-mini-cart-bar' &&
            !barraCandidata.closest('#koi-mini-cart-bar') &&
            !barraCandidata.closest('#shatokb-koi-wrapper')) {
          _barraExternaEl = barraCandidata;
          _ocultarBarraExterna();
        }
      }

      // ── 2. Leer precio actual del quiz ───────────────────────
      // Fuente primaria: #stk-total-amount (precio con 25% OFF)
      // y #stk-total-amount-orig (precio original tachado)
      var totalEl      = document.getElementById('stk-total-amount');
      var totalOrigEl2 = document.getElementById('stk-total-amount-orig');
      var precioActual     = totalEl      ? totalEl.textContent.trim()      : '';
      var precioOrigActual = totalOrigEl2 ? totalOrigEl2.textContent.trim() : '';

      // Fuente secundaria: window.shatokbState → calcular en vivo
      if (!precioActual && window.shatokbState && window.SHATOKB_CATALOGO) {
        var total = 0;
        Object.values(window.shatokbState.selectedProducts || {}).forEach(function (prodId) {
          var prod = window.SHATOKB_CATALOGO.find(function (p) { return p.id === prodId; });
          if (prod) total += prod.precio_num || 0;
        });
        if (total > 0) {
          precioOrigActual = '$' + total.toFixed(2);
          precioActual     = '$' + (total * 0.75).toFixed(2);
        }
      }

      // Fuente terciaria: buscar cualquier elemento con precio en el DOM del quiz
      if (!precioActual) {
        var posiblesPrecios = document.querySelectorAll(
          '#stk-total-bar [class*="amount"], #stk-total-bar [class*="price"], ' +
          '#stk-total-bar [class*="total"], .stk-total-bar__amount'
        );
        posiblesPrecios.forEach(function (el) {
          if (!precioActual) {
            var match = (el.textContent || '').match(/\$[\d,]+\.?\d*/);
            if (match) precioActual = match[0];
          }
        });
      }

      // Solo actualizar si el precio cambió (evita renders innecesarios)
      if (!precioActual || precioActual === _precioAnterior) return;
      _precioAnterior = precioActual;

      var label = 'Estimated total for your routine';
      var cta   = '🛒 Add my full routine to cart';
      _actualizarMiniCartBar(precioActual, label, cta, precioOrigActual);

    }, 600);
  })();

  // ── Wrap de shatokbActualizarTotal ───────────────────────
  // Captura cada vez que el quiz actualiza el total (al cambiar
  // producto) y fuerza sincronización inmediata sin esperar al
  // próximo tick del polling.
  setTimeout(function () {
    if (typeof window.shatokbActualizarTotal === 'function') {
      var _fnOriginalTotal = window.shatokbActualizarTotal;
      window.shatokbActualizarTotal = function () {
        _fnOriginalTotal.apply(this, arguments);
        // Leer ambos precios recién escritos por la función original
        setTimeout(function () {
          var totalEl     = document.getElementById('stk-total-amount');
          var totalOrigEl = document.getElementById('stk-total-amount-orig');
          var precio      = totalEl     ? totalEl.textContent.trim()     : '';
          var precioOrig  = totalOrigEl ? totalOrigEl.textContent.trim() : '';
          if (precio) _actualizarMiniCartBar(precio, 'Estimated total for your routine', '🛒 Add my full routine to cart', precioOrig);
        }, 50);
      };
    }
  }, 1500);

  /* ══════════════════════════════════════════════════════════
     GATE 3 — EMAIL GATE AL HACER ADD AL CARRITO
     Se muestra cuando el usuario pulsa ADD (Starter Kit o
     Full Routine) si aún no ha dado su email.
     Modal 100% autosuficiente — no depende del panel KOI.
     ══════════════════════════════════════════════════════════ */
  window.shatokbInterceptarCarrito = function (callbackProcederAlCarrito) {
    console.log('%c[KOI Gate3] shatokbInterceptarCarrito LLAMADO ✅', 'color:#ec95b8;font-weight:bold;font-size:12px');
    // Si ya tenemos el email, ir directo al carrito
    var email = '';
    try { email = localStorage.getItem('shatokb_email') || ''; } catch (_) {}
    if (!email && typeof KOI_STATE !== 'undefined') {
      email = KOI_STATE.emailCaptured || '';
    }
    if (email) {
      console.log('%c[KOI Gate3] Email ya capturado — bypass modal → carrito directo', 'color:#4ade80;font-size:11px');
      callbackProcederAlCarrito();
      return;
    }
    console.log('%c[KOI Gate3] Sin email → mostrando modal de captura', 'color:#fbbf24;font-size:11px');

    // Inyectar CSS (solo una vez)
    if (!document.getElementById('koi-g3-style')) {
      var st = document.createElement('style');
      st.id = 'koi-g3-style';
      st.textContent =
        '#koi-g3-bd{position:fixed!important;inset:0!important;z-index:2147483640!important;background:rgba(0,0,0,0.75)!important;}' +
        '#koi-g3-modal{position:fixed!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)!important;z-index:2147483647!important;width:calc(100vw - 32px)!important;max-width:420px!important;background:#1c181a!important;border:1.5px solid rgba(236,149,184,0.45)!important;border-radius:20px!important;box-shadow:0 24px 80px rgba(0,0,0,0.9)!important;overflow:hidden!important;display:flex!important;flex-direction:column!important;font-family:Arimo,Arial,sans-serif!important;}' +
        '#koi-g3-body{padding:28px 24px 20px!important;display:flex!important;flex-direction:column!important;gap:16px!important;}' +
        '#koi-g3-avatar-row{display:flex!important;align-items:center!important;gap:10px!important;}' +
        '#koi-g3-avatar{width:42px!important;height:42px!important;border-radius:50%!important;background:linear-gradient(135deg,#b83280,#ec95b8)!important;display:flex!important;align-items:center!important;justify-content:center!important;font-size:22px!important;flex-shrink:0!important;}' +
        '#koi-g3-name{font-size:13px!important;font-weight:700!important;color:#ec95b8!important;}' +
        '#koi-g3-bubble{background:rgba(236,149,184,0.07)!important;border-radius:0 14px 14px 14px!important;border:1px solid rgba(236,149,184,0.15)!important;padding:16px 18px!important;}' +
        '#koi-g3-headline{font-size:17px!important;font-weight:700!important;color:#f5e6ef!important;margin:0 0 10px!important;line-height:1.3!important;}' +
        '#koi-g3-sub{font-size:14px!important;color:rgba(240,220,230,0.85)!important;margin:0 0 8px!important;line-height:1.5!important;}' +
        '#koi-g3-ask{font-size:14px!important;color:rgba(240,220,230,0.85)!important;margin:0!important;line-height:1.5!important;}' +
        '#koi-g3-input{width:100%!important;box-sizing:border-box!important;background:#13100f!important;border:1.5px solid rgba(236,149,184,0.35)!important;border-radius:10px!important;padding:14px 16px!important;font-size:16px!important;color:#f5e6ef!important;outline:none!important;font-family:Arimo,Arial,sans-serif!important;}' +
        '#koi-g3-input:focus{border-color:rgba(236,149,184,0.75)!important;}' +
        '#koi-g3-btn{width:100%!important;box-sizing:border-box!important;background:linear-gradient(135deg,#b83280,#ec95b8)!important;border:none!important;border-radius:10px!important;padding:15px!important;font-size:16px!important;font-weight:700!important;color:#fff!important;cursor:pointer!important;font-family:Prompt,Arial,sans-serif!important;}' +
        '#koi-g3-footer{padding:10px 24px 18px!important;border-top:1px solid rgba(255,255,255,0.07)!important;text-align:center!important;}' +
        '#koi-g3-note{font-size:11px!important;color:rgba(255,255,255,0.38)!important;}';
      document.head.appendChild(st);
    }

    // Bloquear scroll del body
    var prevBodyOverflow = document.body.style.overflow;
    var prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Backdrop
    var bd = document.createElement('div');
    bd.id = 'koi-g3-bd';
    document.body.appendChild(bd);

    // Modal
    var modal = document.createElement('div');
    modal.id = 'koi-g3-modal';
    modal.innerHTML =
      '<div id="koi-g3-body">' +
        '<div id="koi-g3-avatar-row">' +
          '<div id="koi-g3-avatar">\uD83C\uDF38</div>' +
          '<div id="koi-g3-name">KOI</div>' +
        '</div>' +
        '<div id="koi-g3-bubble">' +
          '<p id="koi-g3-headline">\uD83D\uDD2C One last step before your cart</p>' +
          '<p id="koi-g3-sub">I built your <strong>personalized Skin Report</strong> \u2014 your skin analysis and your <strong>step-by-step AM/PM routine</strong>.</p>' +
          '<p id="koi-g3-ask">Where should I send it? <strong style="color:#ec95b8;">It expires in 24\u00a0hours.</strong></p>' +
        '</div>' +
        '<input type="email" id="koi-g3-input" placeholder="you@email.com" autocomplete="email" inputmode="email" />' +
        '<button id="koi-g3-btn" type="button">Send my report \u2192</button>' +
      '</div>' +
      '<div id="koi-g3-footer"><span id="koi-g3-note">\uD83D\uDD12 Only for your Skin Report. No spam. Unsubscribe anytime.</span></div>';
    document.body.appendChild(modal);

    function cerrar() {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      try { if (bd.parentNode) bd.remove(); } catch (_) {}
      try { if (modal.parentNode) modal.remove(); } catch (_) {}
    }

    var btn = document.getElementById('koi-g3-btn');
    var inp = document.getElementById('koi-g3-input');

    btn.addEventListener('click', function () {
      var val = inp ? inp.value.trim() : '';
      if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        if (inp) { inp.style.borderColor = '#f87171'; setTimeout(function () { inp.style.borderColor = ''; }, 800); }
        return;
      }
      btn.disabled = true;
      btn.textContent = '\u23F3 Sending\u2026';
      try { localStorage.setItem('shatokb_email', val); } catch (_) {}
      if (typeof KOI_STATE !== 'undefined') KOI_STATE.emailCaptured = val;
      if (typeof window.shatokbEnviarSkinReportGlobal === 'function') {
        try { window.shatokbEnviarSkinReportGlobal(val).catch(function () {}); } catch (_) {}
      }
      try { if (typeof fbq === 'function') fbq('track', 'Lead', { content_name: 'cart_email_gate', content_category: 'skin_quiz' }); } catch (_) {}
      cerrar();
      callbackProcederAlCarrito();
    });

    if (inp) {
      inp.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); btn.click(); }
      });
      setTimeout(function () { try { if (window.innerWidth > 768) inp.focus(); } catch (_) {} }, 200);
    }
  };

})();
